import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import EmptyState from "./EmptyState";
import FilterBar from "./FilterBar";
import FolderRow from "./FolderRow";
import ProjectCard from "./ProjectCard";
import ProjectGrid from "./ProjectGrid";
import TranscribedFileTable from "./TranscribedFileTable";
import TrebbleInfiniteList from "../common/InfiniteList";
import TrebbleSkeleton from "../common/TrebbleSkeleton";
import TrebbleButton from "../common/TrebbleButton";
import TrebbleFilterButtons from "../common/TrebbleFilterButtons";
import TrebbleInput from "../common/TrebbleInput";
import TrebbleTimeline from "../common/TrebbleTimeline";
import TrebbleBreadcrumb from "../common/TrebbleBreadcrumb";
import LibrarySearchBar from "./LibrarySearchBar";
import LibraryToolbar from "./LibraryToolbar";
import LibraryFilterPopup from "./LibraryFilterPopup";
import LibrarySearchResults from "./LibrarySearchResults";
import PersistentModels from "../../services/PersistentModels";
import TrebbleClientAPI from "../../models/helper/TrebbleClientAPI";
import LocalStorageHelper from "../../models/helper/LocalStorageHelper";
import {
  EXPECTED_ERROR_CATEGORY,
  EXPECTED_ERROR_CLASSIFICATION,
  EXPECTED_ERROR_SOURCE,
} from "../../models/helper/ExpectedError";
import Utils from "../../models/helper/Utils";
import ti18n from "i18n!nls/MyLibraryi18n";
import moment from "moment";
import FolderNameModal from "../common/FolderNameModal";
import {
  LIBRARY_ITEM_TYPES,
  LibraryItem,
  LibraryItemType,
  CapsuleLibraryItem,
  ProjectCardMenuAction,
  getCapsuleIdForItem,
  getFolderAssignmentId,
  getLibraryItemDownloadAudioUrl,
  getLibraryItemPlaybackAudioUrl,
  getLibraryItemDisplayTimestamp,
  getLibraryItemDisplayTitle,
  getOpenableProjectId,
  stampItemType,
} from "./LibraryItemTypes";
import "./styles.css";

// Window globals used by this file. Declared here to avoid `as any` casts.
interface TrebbleAnalyticsHelper {
  trackEvent: (
    category: string,
    action: string,
    label?: string | null,
    value?: number | null,
    metadata?: Record<string, unknown>
  ) => void;
}

// Window globals merged with existing declarations across the codebase.
// Match the existing `any` signatures to avoid TS2717 merge conflicts.
declare global {
  interface Window {
    isMobileBrowser?: boolean;
    trebbleAnalyticsHelper?: TrebbleAnalyticsHelper;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    alertErrorMessage: (error: any) => void;
    alertUserErrorMessage?: (error: string) => void;
    alertWarningMessage?: (message: string, subMessage?: string, callback?: () => void, buttonLabel?: string) => void;
    alertSuccessMessage: (msg: string) => void;
    sendErrorToRaygun?: (error: unknown, critical?: boolean) => void;
    showConfirmDialog: (
      message: string,
      callback: (buttonIndex: number) => void,
      title?: string,
      buttonLabels?: string[],
      cancelCallback?: (() => void) | null,
      extra?: unknown
    ) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getI18n: (i18nData: any, key: string, options?: any) => string;
  }
}

type ActiveSection = "library" | "transcribed" | "history";
type ViewMode = "grid" | "list";

interface Folder {
  id: string;
  name: string;
  color: string;
  itemCount: number;
  parentFolderId?: string | null;
  /**
   * IDs of capsules (or workspace-scope projects) assigned to this folder.
   * Used by search results to resolve the "Location" column for matches
   * that live inside a folder (Bug 2 — previously every search hit
   * displayed the root label regardless of folder membership). Optional
   * because FolderRow/LibraryToolbar and the sibling AssetsLibraryPage
   * declare their own Folder interface without this field, and
   * TypeScript's nominal-looking interface matching rejects structurally
   * identical shapes across files.
   */
  itemIds?: string[];
}

/** Collect a folder ID and all its descendant IDs (to prevent circular moves). */
function getFolderAndDescendantIds(folderId: string, folders: Folder[]): Set<string> {
  const ids = new Set<string>([folderId]);
  let added = true;
  while (added) {
    added = false;
    for (const f of folders) {
      if (f.parentFolderId && ids.has(f.parentFolderId) && !ids.has(f.id)) {
        ids.add(f.id);
        added = true;
      }
    }
  }
  return ids;
}

function getAncestorPath(folder: Folder, folders: Folder[]): Folder[] {
  if (!folder.parentFolderId) return [folder];
  const parent = folders.find(p => p.id === folder.parentFolderId);
  return parent ? [...getAncestorPath(parent, folders), folder] : [folder];
}

const FOLDER_COLORS = [
  "#a8e21d",
  "#c5a3fb",
  "#ffa7d4",
  "#82d9e3",
  "#70A098",
  "#4A90D9",
];

const extractFolderRecords = (response: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(response)) {
    return response as Array<Record<string, unknown>>;
  }
  if (response && typeof response === "object") {
    const responseObject = response as {
      folders?: Array<Record<string, unknown>>;
      items?: Array<Record<string, unknown>>;
      message?: Array<Record<string, unknown>>;
      _id?: unknown;
      id?: unknown;
    };
    if (Array.isArray(responseObject.folders)) {
      return responseObject.folders;
    }
    if (Array.isArray(responseObject.items)) {
      return responseObject.items;
    }
    if (Array.isArray(responseObject.message)) {
      return responseObject.message;
    }
    if (responseObject._id || responseObject.id) {
      return [response as Record<string, unknown>];
    }
  }
  return [];
};

const mapFolderRecord = (folder: Record<string, unknown>, index: number): Folder => {
  const rawItemIds = Array.isArray(folder.itemIds) ? folder.itemIds : [];
  const fallbackColor = FOLDER_COLORS[index % FOLDER_COLORS.length];
  const itemCountValue = typeof folder.itemCount === "number" ? folder.itemCount : rawItemIds.length;
  const normalizedItemIds = rawItemIds
    .map((value) => (value == null ? "" : String(value)))
    .filter((value) => value.length > 0);
  return {
    id: ((folder._id || folder.id || "") as string),
    name: ((folder.name || "Untitled Folder") as string),
    color: ((folder.color || fallbackColor) as string),
    itemCount: itemCountValue,
    parentFolderId: folder.parentFolderId ? String(folder.parentFolderId) : null,
    itemIds: normalizedItemIds,
  };
};

// Type-detection for library items lives in LibraryItemTypes.ts. The
// backend stamps `itemType` on every list response. For older backends
// we run `inferItemTypeForLegacyResponse` once at the API-wrapper layer
// (see stampItemType in fetchProjectsForActiveContext). Within this
// component we only read the discriminator — never sniff shapes.

const getLibraryItemProjectIdOrEmpty = (item: LibraryItem | null | undefined): string => {
  if (!item) {
    return "";
  }
  // NOTE: handlers MUST treat an empty string as "no editable project"
  // (legacy pre-project capsule case). Falling back to the item's _id
  // would send a capsuleId to canAccessProject and trigger a spurious
  // 403 "You do not have access to this project" — see the commit
  // history on this file (c7ece30a0, 3f42aaede) for the bug trail.
  return getOpenableProjectId(item) || "";
};

const getLibraryItemCapsuleIdOrEmpty = (item: LibraryItem | null | undefined): string => {
  if (!item) {
    return "";
  }
  return getCapsuleIdForItem(item) || "";
};

const getLibraryItemFolderAssignmentIdOrEmpty = (
  item: LibraryItem | null | undefined,
  isWorkspaceScoped: boolean,
): string => {
  if (!item) {
    return "";
  }
  return getFolderAssignmentId(item, isWorkspaceScoped) || "";
};

const getLibraryItemTitle = (item: LibraryItem | null | undefined): string => {
  if (!item) {
    return "";
  }
  return getLibraryItemDisplayTitle(item);
};

const getLibraryItemCreatedTimestamp = (item: LibraryItem | null | undefined): number => {
  if (!item) {
    return 0;
  }
  return getLibraryItemDisplayTimestamp(item);
};

const getLibraryItemStableKey = (item: LibraryItem, fallbackIndex: number): string => {
  const rawProjectId = (item as unknown as { projectId?: unknown }).projectId;
  const explicitProjectId = typeof rawProjectId === "string" && rawProjectId.length > 0 ? rawProjectId : null;
  const openableProjectId = getOpenableProjectId(item) || explicitProjectId;
  if (openableProjectId) {
    return `project:${openableProjectId}`;
  }
  const capsuleId = getCapsuleIdForItem(item);
  if (capsuleId) {
    return `capsule:${capsuleId}`;
  }
  const rawId = item._id || item.id;
  return rawId ? `${item.itemType}:${rawId}` : `row:${fallbackIndex}`;
};

const isCapsuleLikeLibraryItem = (item: LibraryItem): boolean => {
  const rawItem = item as unknown as Record<string, unknown>;
  return item.itemType === LIBRARY_ITEM_TYPES.CAPSULE ||
    rawItem.capsuleId !== undefined ||
    rawItem.capsuleUID !== undefined ||
    rawItem.capsuleRefUID !== undefined ||
    rawItem.audioPartsInfo !== undefined;
};

const mergeWorkspaceLibraryItems = (
  workspaceProjects: LibraryItem[],
  workspaceCapsules: LibraryItem[],
): LibraryItem[] => {
  const byKey = new Map<string, LibraryItem>();
  [...workspaceProjects, ...workspaceCapsules].forEach((item, index) => {
    const key = getLibraryItemStableKey(item, index);
    if (!byKey.has(key)) {
      byKey.set(key, item);
      return;
    }
    if (isCapsuleLikeLibraryItem(item)) {
      byKey.set(key, Object.assign({}, byKey.get(key), item, {
        itemType: LIBRARY_ITEM_TYPES.CAPSULE,
      }));
    }
  });
  return Array.from(byKey.values()).sort((itemA, itemB) => (
    getLibraryItemCreatedTimestamp(itemB) - getLibraryItemCreatedTimestamp(itemA)
  ));
};

const normalizeLibraryItemVideoUrl = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }
  return /^(https?:|blob:|data:)/i.test(trimmedValue) ? trimmedValue : null;
};

const getLibraryItemVideoUrl = (item: LibraryItem | null | undefined): string | null => {
  if (!item) {
    return null;
  }
  const processedVideo = item.processedVideo;
  const stringVideoUrl = normalizeLibraryItemVideoUrl(processedVideo);
  if (stringVideoUrl) {
    return stringVideoUrl;
  }
  if (processedVideo && typeof processedVideo === "object") {
    return normalizeLibraryItemVideoUrl((processedVideo as Record<string, unknown>).uri);
  }
  return null;
};

const canDownloadLibraryItem = (item: LibraryItem | null | undefined): boolean => {
  return !!getLibraryItemProjectIdOrEmpty(item);
};

const getLibraryItemVideoThumbnailUrl = (item: LibraryItem | null | undefined): string | null => {
  if (!item) return null;
  const processedVideo = item.processedVideo;
  if (processedVideo && typeof processedVideo === "object") {
    const url = (processedVideo as Record<string, unknown>).thumbnailUrl;
    if (typeof url === "string" && url.trim()) return url.trim();
  }
  return null;
};

interface ProjectCardData {
  id: string;
  capsuleId?: string;   // actual Capsule ID for thumbnail API calls (distinct from display id)
  name: string;
  type: "audio" | "video";
  videoUrl?: string;
  thumbnailUrl?: string;
  audioUrl?: string;
  duration?: string;
  date: string;
  creator?: {
    name: string;
    initials: string;
    color: string;
  };
  thumbnailColor?: string;
  status?: "ready" | "processing";
}

interface TranscribedFile {
  id: string;
  filename: string;
  type: "audio" | "video";
  date: string;
  language: string;
  duration: string;
  status: "Completed" | "Failed" | "Initialized";
}

interface EditHistoryVersion {
  version: number;
  date: string;
  type: string;
}

interface EditHistoryEntry {
  id: string;
  name: string;
  saved: boolean;
  editCount: number;
  lastEdit: string;
  lastEditRelative: string;
  versions: EditHistoryVersion[];
  moreCount: number;
}

const t = (key: string): string => {
  return window.getI18n(ti18n, key);
};

const isExpectedDeleteErrorWithMessage = (
  error: unknown,
): error is Record<string, unknown> & { message: string } => {
  if (!error || typeof error !== "object") return false;
  const record = error as Record<string, unknown>;
  return typeof record.message === "string"
    && (
      record.name === "ExpectedError"
      || record.isNetworkError === true
      || !!record.expectedErrorCategory
    );
};

const isNoResponseStatusZeroFailure = (error: Record<string, unknown>): boolean => {
  const statusText = typeof error.statusText === "string" ? error.statusText.toLowerCase() : "";
  return (error.status === 0 || error.status === "0")
    && statusText !== "abort"
    && statusText !== "timeout"
    && !error.responseJSON
    && !error.responseText;
};

const buildDeleteNoResponseNetworkError = (
  message: string,
  sourceError?: Record<string, unknown>,
): Record<string, unknown> & { message: string } => {
  return {
    name: "ExpectedError",
    message,
    i18nKey: "NO_INTERNET_CONNECTION_AVAILABLE",
    classification: EXPECTED_ERROR_CLASSIFICATION.NETWORK,
    statusCode: 0,
    isNetworkError: true,
    expectedErrorCategory: EXPECTED_ERROR_CATEGORY.TRANSIENT_NETWORK_NO_RESPONSE,
    watchInAnalytics: true,
    source: EXPECTED_ERROR_SOURCE.LIBRARY_PAGE_DELETE_SONG_CAPSULE,
    statusText: sourceError && typeof sourceError.statusText === "string" ? sourceError.statusText : null,
    readyState: sourceError && Object.prototype.hasOwnProperty.call(sourceError, "readyState")
      ? sourceError.readyState
      : null,
  };
};

const normalizeDeleteErrorForReporting = (
  error: unknown,
  fallbackMessage: string,
  networkMessage: string,
): unknown => {
  if (isExpectedDeleteErrorWithMessage(error)) return error;
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    if (isNoResponseStatusZeroFailure(record)) {
      return buildDeleteNoResponseNetworkError(networkMessage, record);
    }
  }
  if (typeof error === "string" && error === networkMessage) {
    return buildDeleteNoResponseNetworkError(networkMessage);
  }
  return error || new Error(fallbackMessage);
};

const getDeleteUserFacingErrorMessage = (reportingError: unknown, fallbackMessage: string): string => {
  return isExpectedDeleteErrorWithMessage(reportingError) ? reportingError.message : fallbackMessage;
};

const mapJobStatus = (status: string): "Completed" | "Failed" | "Initialized" => {
  if (status === "COMPLETED" || status === "completed") return "Completed";
  if (status === "FAILED" || status === "failed" || status === "ERROR") return "Failed";
  return "Initialized";
};

/* ----------------------------------------------------------------
   Inline SVG icons for the sidebar navigation
   ---------------------------------------------------------------- */
const FolderIcon: React.FC<{ color?: string }> = ({ color = "currentColor" }) => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 4.5V12h12V6H8L6.5 4.5H2z"
      stroke={color}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const FileIcon: React.FC<{ color?: string }> = ({ color = "currentColor" }) => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 1.5h5l4 4V14.5H4V1.5z"
      stroke={color}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M9 1.5v4h4" stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const ClockIcon: React.FC<{ color?: string }> = ({ color = "currentColor" }) => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke={color} strokeWidth="1.2" />
    <path d="M8 4.5V8l2.5 1.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ----------------------------------------------------------------
   Filter / sort config per section
   ---------------------------------------------------------------- */
const LIBRARY_FILTERS = [
  { key: "all", label: "All" },
  { key: "audio", label: "Audio" },
  { key: "video", label: "Video" },
];

const LIBRARY_SORTS = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "name_asc", label: "Name A-Z" },
  { key: "name_desc", label: "Name Z-A" },
];

const TRANSCRIBED_FILTERS = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

const PAGE_SIZE = 30;
const WORKSPACE_FILTER_ALL = "all";
const WORKSPACE_FILTER_OWNED = "ownedByMe";
const HISTORY_METADATA_PAGE_SIZE = 100;
const DEFAULT_PROJECT_MENU_ACTIONS = ["edit", "duplicate", "download", "share", "comments", "move", "delete"] as const;
const PROJECT_MENU_ACTIONS_WITHOUT_CAPSULE = ["edit", "duplicate", "download", "move"] as const;
const PROJECT_MENU_ACTIONS_NON_OPENABLE_CAPSULE = ["share", "comments", "move", "delete"] as const;
// Legacy capsules created via "Continue without editing" never got a project
// linked. They expose a Transcribe & Edit entry that runs them through the
// transcription pipeline so a project gets created on demand.
const LEGACY_CAPSULE_REQUIRES_TRANSCRIPTION_MESSAGE_KEY = "LEGACY_CAPSULE_REQUIRES_TRANSCRIPTION";
const LEGACY_CAPSULE_NO_EDITABLE_PROJECT_MESSAGE_KEY = "LEGACY_CAPSULE_NO_EDITABLE_PROJECT";

const formatHistoryTimestamp = (date: Date | null): string => {
  if (!date) {
    return "Untitled";
  }
  return moment(date).format("MMMM D, YYYY h:mm A");
};

type WorkspaceContextValue = {
  workspaceId?: string;
  workspaceName?: string;
  myRole?: string;
  isAdmin?: boolean;
  workspaceStatus?: string;
} | null;

type WorkspaceLibraryState = {
  context: WorkspaceContextValue;
  filter: string | null;
};

const isWorkspaceFeatureEnabled = (): boolean => {
  try {
    const rollout = (window as unknown as Record<string, unknown>).FeatureRolloutHelper as
      { isFeatureEnabled?: (feature: unknown) => boolean; FEATURES?: Record<string, unknown> } | undefined;
    if (rollout && typeof rollout.isFeatureEnabled === "function" && rollout.FEATURES) {
      return rollout.isFeatureEnabled(rollout.FEATURES.WORKSPACE_ENABLED);
    }
    return false;
  } catch {
    return false;
  }
};

const getStoredWorkspaceContext = (): WorkspaceContextValue => {
  try {
    if (!isWorkspaceFeatureEnabled()) {
      return null;
    }
    return LocalStorageHelper.getInstance().getWorkspaceContext() as WorkspaceContextValue;
  } catch {
    return null;
  }
};

const getStoredWorkspaceProjectFilter = (workspaceId: string | null): string | null => {
  if (!workspaceId) {
    return null;
  }
  try {
    const storedFilter = LocalStorageHelper.getInstance().getWorkspaceProjectFilter();
    return storedFilter === WORKSPACE_FILTER_OWNED ? storedFilter : null;
  } catch {
    return null;
  }
};

const getStoredWorkspaceLibraryState = (): WorkspaceLibraryState => {
  const context = getStoredWorkspaceContext();
  const workspaceId = context && context.workspaceId ? context.workspaceId : null;
  return {
    context,
    filter: getStoredWorkspaceProjectFilter(workspaceId),
  };
};

const requestStopInlinePlayback = (): void => {
  try {
    window.dispatchEvent(new Event("trebble:library-stop-inline-playback"));
  } catch {
    // Ignore browsers without CustomEvent support; page hide still unmounts
    // or pauses media in the card cleanup path.
  }
};

/* ----------------------------------------------------------------
   Main component
   ---------------------------------------------------------------- */
interface LibraryPageProps {
  viewMode?: ViewMode;
  section?: ActiveSection;
  pageMode?: "default" | "sharedWithMe";
}

const LibraryPage: React.FC<LibraryPageProps> = ({ viewMode: externalViewMode, section: externalSection, pageMode = "default" }) => {
  /* ---- state ---- */
  const activeSection = externalSection || "library";
  const isSharedWithMeMode = pageMode === "sharedWithMe";
  const [workspaceLibraryState, setWorkspaceLibraryState] = useState<WorkspaceLibraryState>(() => getStoredWorkspaceLibraryState());
  const workspaceContext = workspaceLibraryState.context;
  const currentWorkspaceId = workspaceContext && workspaceContext.workspaceId ? workspaceContext.workspaceId : null;
  const isInWorkspaceContext = !isSharedWithMeMode && !!currentWorkspaceId;
  const workspaceProjectFilter = workspaceLibraryState.filter;
  const activeWorkspaceProjectFilter = workspaceProjectFilter || WORKSPACE_FILTER_ALL;
  const [viewMode, setViewMode] = useState<ViewMode>(externalViewMode || "grid");
  const [searchQuery, setSearchQuery] = useState("");
  /* appliedSearch only updates on explicit submit (Enter / click "Search for…").
     Typing updates searchQuery for the suggestion popup but does NOT filter page content. */
  const [appliedSearch, setAppliedSearch] = useState("");
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    // If user clears the search bar, also clear applied search and exit search results view
    if (!query.trim()) {
      setAppliedSearch("");
      setIsSearchResultsView(false);
      setSearchResults([]);
    }
  }, []);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeSort, setActiveSort] = useState("newest");
  const [sidebarOpen, setSidebarOpen] = useState(!window.isMobileBrowser);
  const [isSearchResultsView, setIsSearchResultsView] = useState(false);
  const [searchResults, setSearchResults] = useState<LibraryItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [folders, setFolders] = useState<Folder[]>([]);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [folderMutationTick, setFolderMutationTick] = useState(0);
  const [folderNameModalOpen, setFolderNameModalOpen] = useState(false);
  const [pendingMoveItemId, setPendingMoveItemId] = useState<string | null>(null);
  const [pendingMoveFolderId, setPendingMoveFolderId] = useState<string | null>(null);
  const [renameFolderModalOpen, setRenameFolderModalOpen] = useState(false);
  const [pendingRenameFolder, setPendingRenameFolder] = useState<Folder | null>(null);
  const [pendingMoveFolder, setPendingMoveFolder] = useState<Folder | null>(null);
  const [transcribedFiles, setTranscribedFiles] = useState<TranscribedFile[]>([]);
  const [historyFilter, setHistoryFilter] = useState<"all" | "unsaved" | "saved">("all");
  const [expandedRefIds, setExpandedRefIds] = useState<Set<string>>(new Set());
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);
  const [folderPath, setFolderPath] = useState<Folder[]>([]);

  const [loading, setLoading] = useState(false);

  /* Cached capsule list for search suggestions (library section) */
  const [cachedCapsules, setCachedCapsules] = useState<LibraryItem[]>([]);
  const [allCachedCapsules, setAllCachedCapsules] = useState<LibraryItem[]>([]);
  const [workspaceOwnerInfoById, setWorkspaceOwnerInfoById] = useState<Map<string, Record<string, unknown>>>(new Map());

  /* Saved project IDs — fetched once to cross-reference edit history.
     Used to mark rows as "saved" and filter out saved rows for the
     "Unsaved drafts" tab. Titles come from the capsule list. */
  const [savedProjectIds, setSavedProjectIds] = useState<Set<string>>(new Set());
  const [capsuleNames, setCapsuleNames] = useState<Map<string, string>>(new Map());
  const [savedHistoryMetadataReady, setSavedHistoryMetadataReady] = useState(false);

  useEffect(() => {
    const syncWorkspaceLibraryState = () => {
      setWorkspaceLibraryState(getStoredWorkspaceLibraryState());
      setActiveFolder(null);
      setFolderPath([]);
    };

    syncWorkspaceLibraryState();
    window.addEventListener("trebble:workspace-library-updated", syncWorkspaceLibraryState);

    return () => {
      window.removeEventListener("trebble:workspace-library-updated", syncWorkspaceLibraryState);
    };
  }, []);

  useEffect(() => {
    setIsSearchResultsView(false);
    setSearchResults([]);
    setSearchQuery("");
    setAppliedSearch("");
  }, [currentWorkspaceId, workspaceProjectFilter]);

  /* ---- derived ---- */
  const userName = (() => {
    try {
      const lsh = LocalStorageHelper.getInstance();
      const name = lsh.getUserFirstName() as string | undefined;
      return name || "User";
    } catch {
      return "User";
    }
  })();

  const workspaceName = isInWorkspaceContext
    ? (workspaceContext?.workspaceName || t("PROJECTS"))
    : t("PROJECTS");

  const getWorkspaceAwareRootLabel = useCallback((): string => {
    if (isSharedWithMeMode) {
      return t("SHARED_WITH_ME");
    }
    return t("PROJECTS");
  }, [isSharedWithMeMode]);

  const getWorkspaceAwareProjectLabel = useCallback((): string => {
    if (isSharedWithMeMode) {
      return t("SHARED_WITH_ME");
    }
    if (!isInWorkspaceContext) {
      return t("PROJECTS");
    }
    if (workspaceProjectFilter === WORKSPACE_FILTER_OWNED) {
      return t("OWNED_BY_ME_TAB");
    }
    return t("PROJECTS");
  }, [isInWorkspaceContext, isSharedWithMeMode, workspaceProjectFilter]);

  const getWorkspaceAwareEmptyMessage = useCallback((): string => {
    if (activeFolder) {
      return t("FOLDER_IS_EMPTY");
    }
    if (isSharedWithMeMode) {
      return t("NO_SHARED_PROJECTS_FOUND");
    }
    if (!isInWorkspaceContext) {
      return t("NO_SHORTCAST");
    }
    if (workspaceProjectFilter === WORKSPACE_FILTER_OWNED) {
      return t("NO_OWNED_PROJECTS_FOUND");
    }
    return t("NO_SHORTCAST");
  }, [activeFolder, isInWorkspaceContext, isSharedWithMeMode, workspaceProjectFilter]);

  const canManageFolders = !isSharedWithMeMode;
  const shouldShowFolderSection = activeSection === "library" && !isSharedWithMeMode && (folders.length > 0 || canManageFolders);

  const setWorkspaceProjectScope = useCallback((nextFilter: string) => {
    const normalizedFilter = nextFilter === WORKSPACE_FILTER_ALL ? null : nextFilter;
    try {
      LocalStorageHelper.getInstance().setWorkspaceProjectFilter(normalizedFilter);
    } catch (error) {
      console.error("Failed to persist workspace project filter:", error);
    }

    setWorkspaceLibraryState((currentState) => ({
      ...currentState,
      filter: normalizedFilter,
    }));
    setActiveFolder(null);
    setFolderPath([]);
  }, []);

  const normalizeProjectListResponse = useCallback((response: unknown): Array<Record<string, unknown>> => {
    if (Array.isArray(response)) {
      return response as Array<Record<string, unknown>>;
    }
    if (response && typeof response === "object") {
      const responseObject = response as {
        projects?: Array<Record<string, unknown>>;
        items?: Array<Record<string, unknown>>;
        message?: Array<Record<string, unknown>>;
      };
      if (Array.isArray(responseObject.projects)) {
        return responseObject.projects;
      }
      if (Array.isArray(responseObject.items)) {
        return responseObject.items;
      }
      if (Array.isArray(responseObject.message)) {
        return responseObject.message;
      }
    }
    return [];
  }, []);

  const normalizeSharedProjectListResponse = useCallback((response: unknown): Array<Record<string, unknown>> => {
    if (Array.isArray(response)) {
      return response as Array<Record<string, unknown>>;
    }
    if (response && typeof response === "object") {
      const responseObject = response as {
        projects?: Array<Record<string, unknown>>;
        items?: Array<Record<string, unknown>>;
        message?: Array<Record<string, unknown>>;
      };
      if (Array.isArray(responseObject.projects)) {
        return responseObject.projects;
      }
      if (Array.isArray(responseObject.items)) {
        return responseObject.items;
      }
      if (Array.isArray(responseObject.message)) {
        return responseObject.message;
      }
    }
    return [];
  }, []);

  const normalizeFolderListResponse = useCallback((response: unknown): Folder[] => {
    return extractFolderRecords(response)
      .map((folder, index) => mapFolderRecord(folder, index))
      .filter((folder) => !!folder.id);
  }, []);

  const loadFolders = useCallback(async () => {
    if (isSharedWithMeMode) {
      setFolders([]);
      setActiveFolder(null);
      setFolderPath([]);
      return;
    }
    try {
      const api = TrebbleClientAPI.getInstance();
      const rawFolders = await api.listProjectFolders({
        workspaceId: currentWorkspaceId || undefined,
        parentFolderId: activeFolder?.id || null,
      });
      const nextFolders = normalizeFolderListResponse(rawFolders);
      setFolders(nextFolders);
    } catch (error) {
      console.error("Failed to load folders:", error);
      setFolders([]);
    }
  }, [activeFolder?.id, currentWorkspaceId, isSharedWithMeMode, normalizeFolderListResponse]);

  const loadAllFolders = useCallback(async () => {
    try {
      const response = await TrebbleClientAPI.getInstance().listProjectFolders({
        workspaceId: currentWorkspaceId || undefined,
      });
      const normalized = normalizeFolderListResponse(response);
      setAllFolders(normalized);
      return normalized;
    } catch (err) {
      console.error("Failed to load all folders:", err);
      return [];
    }
  }, [currentWorkspaceId, normalizeFolderListResponse]);

  const fetchProjectsForActiveContext = useCallback(async (
    query: string,
    pageOffset: number,
    pageSize: number,
    options?: { folderId?: string | null }
  ): Promise<LibraryItem[]> => {
    const api = TrebbleClientAPI.getInstance();
    const folderId = options?.folderId || null;
    if (isSharedWithMeMode) {
      const sharedProjects = await api.getProjectsSharedWithMe({
        pageOffset,
        numberOfResults: pageSize,
      });
      const normalizedSharedProjects = normalizeSharedProjectListResponse(sharedProjects);
      // Stamp itemType for legacy backends that don't send the
      // discriminator yet. New backends already include itemType on
      // every entry; stampItemType preserves the existing value.
      const stamped = stampItemType(normalizedSharedProjects, LIBRARY_ITEM_TYPES.SHARED_PROJECT);
      if (!query || !query.trim()) {
        return stamped;
      }
      const lowerQuery = query.trim().toLowerCase();
      return stamped.filter((project) => {
        if (project.itemType !== LIBRARY_ITEM_TYPES.SHARED_PROJECT) {
          return getLibraryItemDisplayTitle(project).toLowerCase().includes(lowerQuery);
        }
        const projectName = (project.project && project.project.name) || project.name || "";
        const owner = project.owner;
        const ownerName = (owner && owner.username) || project.ownerUsername || "";
        return projectName.toLowerCase().includes(lowerQuery) || ownerName.toLowerCase().includes(lowerQuery);
      });
    }
    const isRootLevel = !folderId;
    if (isInWorkspaceContext && currentWorkspaceId) {
      const fetchLimit = Math.max(pageSize, (pageOffset + 1) * pageSize);
      const startIndex = pageOffset * pageSize;
      const endIndex = startIndex + pageSize;
      const [workspaceCapsulesResponse, workspaceProjectsResponse] = await Promise.all([
        api.getAllMyCapsulesWithPaging(
          query,
          0,
          fetchLimit,
          workspaceProjectFilter === WORKSPACE_FILTER_OWNED ? WORKSPACE_FILTER_OWNED : undefined,
          undefined,
          folderId || undefined,
          isRootLevel && !query,
          currentWorkspaceId,
        ) as Promise<Array<Record<string, unknown>> | null>,
        api.listWorkspaceProjects({
          workspaceId: currentWorkspaceId,
          searchKeyword: query || null,
          pageOffset: 0,
          numberOfResults: fetchLimit,
          referenceProjectOnly: true,
          folderId: folderId || undefined,
          unfiledOnly: isRootLevel && !query,
          ownedByMe: workspaceProjectFilter === WORKSPACE_FILTER_OWNED,
        }) as Promise<unknown>,
      ]);
      const workspaceCapsules = stampItemType(workspaceCapsulesResponse || [], LIBRARY_ITEM_TYPES.CAPSULE);
      const workspaceProjects = stampItemType(
        normalizeProjectListResponse(workspaceProjectsResponse),
        LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT,
      );
      return mergeWorkspaceLibraryItems(workspaceProjects, workspaceCapsules).slice(startIndex, endIndex);
    }
    const personalProjects = await api.getAllMyCapsulesWithPaging(
      query,
      pageOffset,
      pageSize,
      undefined,
      undefined,
      folderId || undefined,
      isRootLevel && !query,
    ) as Array<Record<string, unknown>> | null;
    return stampItemType(personalProjects || [], LIBRARY_ITEM_TYPES.CAPSULE);
  }, [currentWorkspaceId, isInWorkspaceContext, isSharedWithMeMode, normalizeProjectListResponse, normalizeSharedProjectListResponse, workspaceProjectFilter]);

  const fetchSavedProjectsForHistory = useCallback(async (
    query: string,
    pageOffset: number,
    pageSize: number,
  ): Promise<Array<Record<string, unknown>>> => {
    const api = TrebbleClientAPI.getInstance();
    if (isInWorkspaceContext && currentWorkspaceId) {
      const workspaceProjects = await api.listWorkspaceProjects({
        workspaceId: currentWorkspaceId,
        searchKeyword: query || null,
        pageOffset,
        numberOfResults: pageSize,
        referenceProjectOnly: true,
      });
      return normalizeProjectListResponse(workspaceProjects);
    }

    const personalProjects = await api.getAllMyCapsulesWithPaging(
      query,
      pageOffset,
      pageSize,
    ) as Array<Record<string, unknown>> | null;
    return personalProjects || [];
  }, [currentWorkspaceId, isInWorkspaceContext, normalizeProjectListResponse]);

  useEffect(() => {
    if (activeSection !== "library") return;
    fetchProjectsForActiveContext("", 0, 100, { folderId: activeFolder?.id || null }).then((capsules: LibraryItem[]) => {
      setCachedCapsules(capsules || []);
    });
  }, [activeSection, activeFolder?.id, fetchProjectsForActiveContext, folderMutationTick]);

  // Fetch all projects (no folder filter) for search suggestions across the whole workspace
  useEffect(() => {
    if (activeSection !== "library") return;
    fetchProjectsForActiveContext("", 0, 100, { folderId: null }).then((capsules: LibraryItem[]) => {
      setAllCachedCapsules(capsules || []);
    });
  }, [activeSection, fetchProjectsForActiveContext, folderMutationTick]);

  useEffect(() => {
    if (activeSection !== "library") return;
    loadFolders();
    loadAllFolders();
  }, [activeSection, loadFolders, loadAllFolders]);

  useEffect(() => {
    let cancelled = false;

    if (!isInWorkspaceContext || !currentWorkspaceId) {
      setWorkspaceOwnerInfoById(new Map());
      return () => {
        cancelled = true;
      };
    }

    TrebbleClientAPI.getInstance().listWorkspaceMembers({
      workspaceId: currentWorkspaceId,
    }).then((response: unknown) => {
      if (cancelled) {
        return;
      }

      const responseObject = response && typeof response === "object" ? response as {
        members?: Array<Record<string, unknown>>;
        items?: Array<Record<string, unknown>>;
        message?: Array<Record<string, unknown>>;
      } : null;
      const members = Array.isArray(response)
        ? response as Array<Record<string, unknown>>
        : Array.isArray(responseObject?.members)
          ? responseObject.members
          : Array.isArray(responseObject?.items)
            ? responseObject.items
            : Array.isArray(responseObject?.message)
              ? responseObject.message
              : [];

      const nextOwnerInfoById = new Map<string, Record<string, unknown>>();
      members.forEach((member) => {
        const user = member.user as Record<string, unknown> | undefined;
        const userUID = ((user?.userUID || user?._id || member.userUID || "") as string).trim();
        if (!userUID) {
          return;
        }

        nextOwnerInfoById.set(userUID, {
          ...(user || {}),
          name: ((user?.firstName as string) || (user?.username as string) || (member.email as string) || "") as string,
        });
      });

      setWorkspaceOwnerInfoById(nextOwnerInfoById);
    }).catch((error: unknown) => {
      console.error("Failed to load workspace owner info:", error);
      if (!cancelled) {
        setWorkspaceOwnerInfoById(new Map());
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId, isInWorkspaceContext]);

  useEffect(() => {
    if (activeSection !== "history") return;

    let cancelled = false;
    setSavedHistoryMetadataReady(false);

    const bootstrapSavedHistoryMetadata = async () => {
      const ids = new Set<string>();
      const names = new Map<string, string>();
      let pageOffset = 0;
      // Bound the bootstrap loop — prior code ran an unbounded while(true)
      // that walked the entire capsule list before the /edithistory page
      // could render any rows. Workspaces with hundreds of saved capsules
      // were paying ~10s on page load. A 5-page cap (500 capsules at the
      // current HISTORY_METADATA_PAGE_SIZE of 100) covers every user we
      // have today without pathological fetches; the "saved" tab still
      // paginates its own view for anything beyond.
      const MAX_METADATA_PAGES = 5;

      while (!cancelled && pageOffset < MAX_METADATA_PAGES) {
        const projects = await fetchSavedProjectsForHistory("", pageOffset, HISTORY_METADATA_PAGE_SIZE);
        if (!projects || projects.length === 0) {
          break;
        }

        for (const project of projects) {
          const nestedProject = project.project as Record<string, unknown> | undefined;
          const projectId = (
            project.projectId ||
            project.referenceProjectUID ||
            nestedProject?._id ||
            nestedProject?.projectId
          ) as string | undefined;
          const entryId = (project._id || project.id) as string | undefined;
          const title = (project.title || project.name || nestedProject?.name || "") as string;

          if (projectId) {
            ids.add(projectId);
            if (title && !names.has(projectId)) {
              names.set(projectId, title);
            }
          }

          if (entryId) {
            ids.add(entryId);
            if (title && !names.has(entryId)) {
              names.set(entryId, title);
            }
          }
        }

        if (projects.length < HISTORY_METADATA_PAGE_SIZE) {
          break;
        }

        pageOffset += 1;
      }

      if (cancelled) {
        return;
      }

      setSavedProjectIds(ids);
      setCapsuleNames(names);
      setSavedHistoryMetadataReady(true);
    };

    bootstrapSavedHistoryMetadata().catch((error) => {
      console.error("Failed to bootstrap saved history metadata:", error);
      if (cancelled) {
        return;
      }
      setSavedProjectIds(new Set());
      setCapsuleNames(new Map());
      setSavedHistoryMetadataReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [activeSection, fetchSavedProjectsForHistory]);

  /* ---- data fetching ---- */

  const sortSharedProjects = useCallback((projects: LibraryItem[]): LibraryItem[] => {
    const sortedProjects = [...projects];

    if (activeSort === "oldest") {
      sortedProjects.sort((projectA, projectB) => getLibraryItemCreatedTimestamp(projectA) - getLibraryItemCreatedTimestamp(projectB));
    } else if (activeSort === "name_asc") {
      sortedProjects.sort((projectA, projectB) => getLibraryItemTitle(projectA).localeCompare(getLibraryItemTitle(projectB)));
    } else if (activeSort === "name_desc") {
      sortedProjects.sort((projectA, projectB) => getLibraryItemTitle(projectB).localeCompare(getLibraryItemTitle(projectA)));
    }

    return sortedProjects;
  }, [activeSort]);

  const filterAndSortLibraryProjects = useCallback((projects: LibraryItem[]): LibraryItem[] => {
    if (isSharedWithMeMode) {
      return sortSharedProjects(projects);
    }

    let filteredProjects = [...projects];
    if (activeFilter === "audio") {
      filteredProjects = filteredProjects.filter((project) => !getLibraryItemVideoUrl(project));
    } else if (activeFilter === "video") {
      filteredProjects = filteredProjects.filter((project) => !!getLibraryItemVideoUrl(project));
    }

    if (activeSort === "oldest") {
      filteredProjects.sort((projectA, projectB) => getLibraryItemCreatedTimestamp(projectA) - getLibraryItemCreatedTimestamp(projectB));
    } else if (activeSort === "name_asc") {
      filteredProjects.sort((projectA, projectB) => getLibraryItemTitle(projectA).localeCompare(getLibraryItemTitle(projectB)));
    } else if (activeSort === "name_desc") {
      filteredProjects.sort((projectA, projectB) => getLibraryItemTitle(projectB).localeCompare(getLibraryItemTitle(projectA)));
    }

    return filteredProjects;
  }, [activeFilter, activeSort, isSharedWithMeMode, sortSharedProjects]);

  /* Fetch page of projects — called by TrebbleInfiniteList */
  const fetchLibraryData = useCallback(async (pageOffset: number, pageSize: number) => {
    if (isSharedWithMeMode) {
      const needsClientSearchOrSort = !!appliedSearch.trim() || activeSort !== "newest";
      if (!needsClientSearchOrSort) {
        return fetchProjectsForActiveContext("", pageOffset, pageSize, {
          folderId: null,
        });
      }

      const fetchLimit = Math.max(100, (pageOffset + 1) * pageSize * 3);
      const rawSharedProjects = await fetchProjectsForActiveContext(appliedSearch, 0, fetchLimit, {
        folderId: null,
      });
      const sortedSharedProjects = sortSharedProjects(rawSharedProjects);

      const startIndex = pageOffset * pageSize;
      const endIndex = startIndex + pageSize;
      return {
        items: sortedSharedProjects.slice(startIndex, endIndex),
        hasMore: endIndex < sortedSharedProjects.length,
      };
    }

    // If a non-default filter or sort is active, the BE can't return a
    // globally-correct page (server only supports newest-first and has no
    // audio/video filter — see TrebbleClientAPI.getAllMyCapsulesWithPaging
    // and Capsule.getAllUserCapsules). Falling back to per-page client
    // filter/sort breaks pagination (filtered page < pageSize -> InfiniteList
    // stops) and sort order (per-page only). Mirror the shared-with-me
    // strategy above: fetch enough rows to cover the requested page, apply
    // filter+sort across the whole set, then slice. See TRE-503.
    const needsClientFilter = activeFilter !== "all";
    const needsClientSort = activeSort !== "newest";

    if (needsClientFilter || needsClientSort) {
      const fetchLimit = Math.max(100, (pageOffset + 1) * pageSize * 3);
      const rawProjects = await fetchProjectsForActiveContext(appliedSearch, 0, fetchLimit, {
        folderId: activeFolder?.id || null,
      });
      if (!rawProjects) return { items: [], hasMore: false };
      const filteredAndSorted = filterAndSortLibraryProjects(rawProjects);

      const startIndex = pageOffset * pageSize;
      const endIndex = startIndex + pageSize;
      // hasMore = either we filled the slice OR we hit the BE pagination
      // limit (more rows likely exist server-side). The latter check keeps
      // pagination alive when filterLimit < total matching rows.
      const slicedHasMore = endIndex < filteredAndSorted.length;
      const beLikelyHasMore = rawProjects.length >= fetchLimit;
      return {
        items: filteredAndSorted.slice(startIndex, endIndex),
        hasMore: slicedHasMore || beLikelyHasMore,
      };
    }

    const rawProjects = await fetchProjectsForActiveContext(appliedSearch, pageOffset, pageSize, {
      folderId: activeFolder?.id || null,
    });
    if (!rawProjects) return [];
    return rawProjects;
  }, [activeFilter, activeFolder?.id, activeSort, appliedSearch, fetchProjectsForActiveContext, filterAndSortLibraryProjects, isSharedWithMeMode, sortSharedProjects]);

  /* Map a raw capsule/project/shared-project item to a ProjectCardData for rendering */
  const mapCapsuleToCard = useCallback((item: LibraryItem): ProjectCardData => {
    const sharedProject = item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? item.project : null;
    const sharedOwner = item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? item.owner : null;
    const rawOwnerUID = item.ownerUserUID || (sharedProject ? sharedProject.ownerUserUID : "") || "";
    const ownerUserUID = (typeof rawOwnerUID === "string" ? rawOwnerUID : String(rawOwnerUID || "")).trim();
    const workspaceOwner = ownerUserUID ? workspaceOwnerInfoById.get(ownerUserUID) : undefined;
    const projectId = getLibraryItemProjectIdOrEmpty(item);
    const capsuleId = getLibraryItemCapsuleIdOrEmpty(item);
    const title = getLibraryItemTitle(item) || "Untitled";
    const rawVideoUri = getLibraryItemVideoUrl(item) || undefined;
    // Map the rendered-video URL to CloudFront. The rendered bucket
    // (fm.trebble.video.rendered) has a dotted name, so its virtual-hosted
    // HTTPS URL fails the TLS handshake (cert covers a single label). The
    // thumbnail self-heal in ProjectCard does a browser range read on this
    // URL, which would fail without the CloudFront mapping.
    const videoUri = rawVideoUri
      ? Utils.getInstance().getCloudFrontUrlIfApplicable(rawVideoUri)
      : undefined;
    const rawThumbnailUri = getLibraryItemVideoThumbnailUrl(item) || undefined;
    const thumbnailUri = rawThumbnailUri
      ? Utils.getInstance().getCloudFrontUrlIfApplicable(rawThumbnailUri) +
        (item.lastUpdatedDate ? `?v=${encodeURIComponent(String(item.lastUpdatedDate))}` : "")
      : undefined;
    const hasVideo = !!videoUri;

    // Playback and menu/action availability share the same legacy-shape resolver.
    const audioUri = getLibraryItemPlaybackAudioUrl(item) || undefined;

    const createdDate = item.createdDate || item.creationDate || item.lastUpdatedDate || (item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? item.grantedDate : undefined) || (sharedProject ? sharedProject.lastUpdatedDate : undefined) || (sharedProject ? sharedProject.createdDate : undefined);
    const dateStr = createdDate ? new Date(createdDate as string | number).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

    // Extract creator info
    const creatorInfo = item.creatorInfoJson;
    const itemOwner = (item as unknown as { owner?: Record<string, unknown> | null }).owner;
    let creatorData: ProjectCardData["creator"] | undefined;
    if (creatorInfo || sharedOwner || workspaceOwner || itemOwner) {
      const creatorSource = (creatorInfo || sharedOwner || workspaceOwner || itemOwner) as Record<string, unknown>;
      const creatorName = ((creatorSource?.name as string) || (creatorSource?.fullName as string) || (creatorSource?.firstName as string) || (creatorSource?.username as string) || "") as string;
      const initials = creatorName
        .split(" ")
        .map((w: string) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?";
      // Derive a stable color from the username
      const uname = ((creatorSource?.username as string) || creatorName || "") as string;
      let hash = 0;
      for (let i = 0; i < uname.length; i++) hash = ((hash << 5) - hash + uname.charCodeAt(i)) | 0;
      const creatorColors = ["#a8e21d", "#c5a3fb", "#ffa7d4", "#82d9e3", "#70A098", "#4A90D9"];
      const creatorColor = creatorColors[Math.abs(hash) % creatorColors.length];
      creatorData = {
        name: creatorName,
        initials,
        color: creatorColor,
      };
    }

    return {
      id: projectId || capsuleId,
      capsuleId: capsuleId || undefined,
      name: title,
      type: hasVideo ? "video" as const : "audio" as const,
      videoUrl: videoUri,
      thumbnailUrl: thumbnailUri,
      audioUrl: audioUri,
      duration: (() => {
        const secs = item.durationInSeconds;
        if (secs && secs > 0) {
          const mins = Math.floor(secs / 60);
          const s = Math.floor(secs % 60);
          return `${mins}:${s.toString().padStart(2, "0")}`;
        }
        return undefined;
      })(),
      date: dateStr,
      creator: creatorData,
      status: "ready" as const,
    };
  }, [workspaceOwnerInfoById]);

  const getProjectCardMenuActions = useCallback((project: LibraryItem): ProjectCardMenuAction[] => {
    if (isSharedWithMeMode) {
      return [];
    }

    const hasCapsule = !!getLibraryItemCapsuleIdOrEmpty(project);
    if (!hasCapsule) {
      return [...PROJECT_MENU_ACTIONS_WITHOUT_CAPSULE];
    }

    // Legacy "Continue without editing" capsules carry a capsuleId but no
    // editable project. Replace the Edit/Duplicate options (which would
    // route to a 403) with Transcribe & Edit, matching the master capsule
    // card UX (see PR #1165).
    const hasOpenableProject = !!getLibraryItemProjectIdOrEmpty(project);
    const hasAudioToTranscribe = !!getLibraryItemPlaybackAudioUrl(project);
    const hasAudioToDownload = !!getLibraryItemDownloadAudioUrl(project);
    if (!hasOpenableProject) {
      const actions: ProjectCardMenuAction[] = [...PROJECT_MENU_ACTIONS_NON_OPENABLE_CAPSULE];
      if (hasAudioToDownload) actions.unshift("download");
      if (hasAudioToTranscribe) actions.unshift("transcribe");
      return actions;
    }

    return [...DEFAULT_PROJECT_MENU_ACTIONS];
  }, [isSharedWithMeMode]);

  /* Fetch transcribed files — called by TrebbleInfiniteList */
  const fetchTranscribedData = useCallback(async (pageOffset: number, pageSize: number) => {
    const api = TrebbleClientAPI.getInstance();
    const rawFiles = await api.getTranscribedFiles(
      searchQuery,
      pageOffset,
      pageSize,
      currentWorkspaceId || undefined,
    ) as Array<Record<string, unknown>> | null;
    if (!rawFiles || !Array.isArray(rawFiles)) return [];

    let mapped = rawFiles.map((file: Record<string, unknown>) => {
      const durationMs = (file.exactAudioDurationInMilliseconds || file.audioDurationInMilliseconds || 0) as number;
      const durationSec = durationMs / 1000;
      const mins = Math.floor(durationSec / 60);
      const secs = Math.floor(durationSec % 60);
      const durationStr = durationMs > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : "--:--";

      const jobDate = file.jobCreationDate ? new Date(file.jobCreationDate as string | number) : null;
      const displayName = (file.filenameTranscribed || (jobDate
        ? jobDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })
        : "Untitled")) as string;
      const displayDate = jobDate
        ? jobDate.toLocaleDateString(undefined, { month: "long", day: "numeric" })
        : "";
      const langCode = (file.languageCode || "en") as string;
      const displayLang = (() => {
        try {
          const dn = new Intl.DisplayNames(["en"], { type: "language" });
          return dn.of(langCode) || langCode;
        } catch { return langCode; }
      })();

      return {
        id: (file._id || "") as string,
        filename: displayName,
        type: (file.isVideo ? "video" : "audio") as "audio" | "video",
        date: displayDate,
        language: displayLang,
        duration: durationStr,
        status: mapJobStatus((file.trebbleJobStatus || "") as string),
      };
    });

    // Client-side filter
    if (activeFilter === "completed") mapped = mapped.filter(f => f.status === "Completed");
    else if (activeFilter === "failed") mapped = mapped.filter(f => f.status === "Failed");

    return mapped;
  }, [searchQuery, activeFilter, currentWorkspaceId]);

  /* Legacy fetch for non-infinite transcribed files (kept for backward compat) */
  const fetchTranscribedFiles = useCallback(async () => {
    setLoading(true);
    try {
      const api = TrebbleClientAPI.getInstance();
      const rawFiles = await api.getTranscribedFiles(
        searchQuery,
        0,
        PAGE_SIZE,
        currentWorkspaceId || undefined,
      ) as Array<Record<string, unknown>> | null;
      if (rawFiles && Array.isArray(rawFiles)) {
        const mapped: TranscribedFile[] = rawFiles.map((file: Record<string, unknown>) => {
          const durationMs = (file.exactAudioDurationInMilliseconds || file.audioDurationInMilliseconds || 0) as number;
          const durationSec = durationMs / 1000;
          const mins = Math.floor(durationSec / 60);
          const secs = Math.floor(durationSec % 60);
          const durationStr = durationMs > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : "--:--";

          const jobDate = file.jobCreationDate ? new Date(file.jobCreationDate as string | number) : null;
          const displayName = (file.filenameTranscribed || (jobDate
            ? jobDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })
            : "Untitled")) as string;
          const displayDate = jobDate
            ? jobDate.toLocaleDateString(undefined, { month: "long", day: "numeric" })
            : "";
          const langCode = (file.languageCode || "en") as string;
          const displayLang = (() => {
            try {
              const dn = new Intl.DisplayNames(["en"], { type: "language" });
              return dn.of(langCode) || langCode;
            } catch { return langCode; }
          })();

          return {
            id: (file._id || "") as string,
            filename: displayName,
            type: (file.isVideo ? "video" : "audio") as "audio" | "video",
            date: displayDate,
            language: displayLang,
            duration: durationStr,
            status: mapJobStatus((file.trebbleJobStatus || "") as string),
          };
        });
        setTranscribedFiles(mapped);
      } else {
        setTranscribedFiles([]);
      }
    } catch (error) {
      console.error("Failed to fetch transcribed files:", error);
      setTranscribedFiles([]);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspaceId, searchQuery]);

  /* Fetch grouped edit history — called by TrebbleInfiniteList.
     Uses the server-side grouped endpoint (listEditHistoryGrouped) so we can
     paginate over GROUPS instead of fetching every version doc with a
     client-side while(true) loop. The "saved" filter still uses the capsule
     list (separate data source); "all" search merges saved-title matches when
     needed while "unsaved" keeps the server paging bounded. */
  const fetchHistoryData = useCallback(async (pageOffset: number, pageSize: number) => {
    const api = TrebbleClientAPI.getInstance();
    type EditHistoryEntry = {
      _id: string;
      refId: string;
      name: string;
      saved: boolean;
      editCount: number;
      latestDate: string | number | null;
      latestVersion: number;
      versions: Array<Record<string, unknown>>;
    };
    const mapSavedProjectToHistoryEntry = (project: Record<string, unknown>): EditHistoryEntry => {
      const nestedProject = project.project as Record<string, unknown> | undefined;
      const projectId = (
        project.projectId ||
        project.referenceProjectUID ||
        nestedProject?._id ||
        nestedProject?.projectId ||
        project._id ||
        project.id
      ) as string;
      const title = (project.title || project.name || nestedProject?.name || "") as string;
      const latestDate = (
        project.lastEditedDate ||
        project.lastUpdatedDate ||
        project.updatedDate ||
        project.createdDate ||
        project.creationDate ||
        nestedProject?.lastUpdatedDate ||
        nestedProject?.createdDate
      ) as string | number | undefined;

      return {
        _id: (project._id || project.id || projectId) as string,
        refId: projectId,
        name: title,
        saved: true,
        editCount: 1,
        latestDate: latestDate ?? null,
        latestVersion: (project.version || 1) as number,
        versions: [{
          version: (project.version || 1) as number,
          date: latestDate ?? null,
          _id: (project._id || project.id || projectId) as string,
        }],
      };
    };

    if (historyFilter === "saved") {
      const savedProjects = await fetchSavedProjectsForHistory(searchQuery, pageOffset, pageSize);
      if (!savedProjects || savedProjects.length === 0) return [];

      return savedProjects.map(mapSavedProjectToHistoryEntry);
    }

    if (!savedHistoryMetadataReady) {
      return { items: [], hasMore: false };
    }

    // "unsaved" filter strips groups whose refId is in savedProjectIds. A
    // single oversampled server page can still return fewer than pageSize
    // unsaved entries when most groups are saved, which would short-circuit
    // the infinite scroll. To fix, keep fetching subsequent server pages
    // (advancing pageOffset) until we have at least pageSize unsaved entries
    // or the server reports no more results.
    const needsClientFilter = historyFilter === "unsaved" && savedProjectIds.size > 0;
    const needsSavedTitleSearch = historyFilter === "all" && !!searchQuery.trim();
    const requestedItemCount = needsSavedTitleSearch
      ? Math.max(pageSize, (pageOffset + 1) * pageSize)
      : pageSize;
    const serverPageSize = needsClientFilter
      ? Math.min(pageSize * 3, 100)
      : needsSavedTitleSearch
        ? requestedItemCount
      : pageSize;

    type EditHistoryResponse = { items?: Array<Record<string, unknown>>; hasMore?: boolean; totalCount?: number } | null;

    const aggregatedRawItems: Array<Record<string, unknown>> = [];
    let serverHasMore = false;
    let currentOffset = needsSavedTitleSearch ? 0 : pageOffset;
    const MAX_SERVER_PAGES = 5; // safety cap to avoid runaway loops

    for (let attempt = 0; attempt < MAX_SERVER_PAGES; attempt += 1) {
      let response: EditHistoryResponse;
      try {
        response = await api.listEditHistoryGrouped({
          searchKeyword: searchQuery || undefined,
          pageOffset: currentOffset,
          numberOfResults: serverPageSize,
          workspaceId: currentWorkspaceId || undefined,
        }) as EditHistoryResponse;
      } catch (error) {
        console.error("Edit history fetch failed:", error);
        if (aggregatedRawItems.length === 0) {
          return { items: [], hasMore: false };
        }
        break;
      }

      const pageItems = response && Array.isArray(response.items) ? response.items : [];
      const pageHasMore = !!(response && response.hasMore);
      aggregatedRawItems.push(...pageItems);
      serverHasMore = pageHasMore;

      if (!needsClientFilter) break;

      // Estimate how many unsaved entries we have so far without re-mapping
      // the whole list (refId lookup against savedProjectIds is cheap).
      const unsavedSoFar = aggregatedRawItems.filter((g) => {
        const refId = ((g.refId || "") as string);
        const latestId = (g.latestId || g._id || refId) as string;
        return !(savedProjectIds.has(refId) || savedProjectIds.has(latestId));
      }).length;

      if (unsavedSoFar >= pageSize || !pageHasMore) break;
      currentOffset += pageItems.length || serverPageSize;
    }

    const rawItems = aggregatedRawItems;

    const mapped: EditHistoryEntry[] = rawItems.map((group) => {
      const refId = ((group.refId || "") as string);
      const latestId = (group.latestId || group._id || refId) as string;
      const latestDate = (group.latestDate || null) as string | number | null;
      const latestVersion = (group.latestVersion || 1) as number;
      const editCount = (group.editCount || 1) as number;
      const versions = Array.isArray(group.versions)
        ? (group.versions as Array<Record<string, unknown>>)
        : [];

      const isSaved = savedProjectIds.has(refId) || savedProjectIds.has(latestId);
      const serverName = (group.name || "") as string;
      const capsuleTitle = capsuleNames.get(refId) || capsuleNames.get(latestId) || "";

      return {
        _id: latestId,
        refId,
        name: isSaved ? (capsuleTitle || serverName) : "",
        saved: isSaved,
        editCount,
        latestDate,
        latestVersion,
        versions,
      };
    });

    let combinedHistoryEntries: EditHistoryEntry[] = mapped;
    let savedSearchHasMore = false;

    if (needsSavedTitleSearch) {
      try {
        const savedProjects = await fetchSavedProjectsForHistory(searchQuery, 0, requestedItemCount);
        const savedMatches = (savedProjects || []).map(mapSavedProjectToHistoryEntry);
        savedSearchHasMore = savedMatches.length >= requestedItemCount;

        if (savedMatches.length > 0) {
          const seenHistoryIds = new Set<string>();
          const getHistoryEntryIds = (entry: EditHistoryEntry) =>
            [entry._id, entry.refId].filter((id): id is string => typeof id === "string" && !!id);
          const keepFirstUniqueEntry = (entry: EditHistoryEntry) => {
            const ids = getHistoryEntryIds(entry);
            if (ids.some((id) => seenHistoryIds.has(id))) {
              return false;
            }
            ids.forEach((id) => seenHistoryIds.add(id));
            return true;
          };
          const savedMatchesById = new Map<string, EditHistoryEntry>();
          const uniqueSavedMatches = savedMatches.filter((entry) => {
            const ids = getHistoryEntryIds(entry);
            if (ids.some((id) => savedMatchesById.has(id))) {
              return false;
            }
            ids.forEach((id) => savedMatchesById.set(id, entry));
            return keepFirstUniqueEntry(entry);
          });
          const uniqueHistoryEntries = combinedHistoryEntries.filter((entry) => {
            const matchingSavedEntry = getHistoryEntryIds(entry)
              .map((id) => savedMatchesById.get(id))
              .find((savedEntry): savedEntry is EditHistoryEntry => !!savedEntry);
            if (matchingSavedEntry) {
              Object.assign(matchingSavedEntry, {
                _id: entry._id,
                refId: entry.refId,
                name: matchingSavedEntry.name || entry.name,
                saved: entry.saved || matchingSavedEntry.saved,
                editCount: entry.editCount,
                latestDate: entry.latestDate,
                latestVersion: entry.latestVersion,
                versions: entry.versions,
              });
              return false;
            }
            return keepFirstUniqueEntry(entry);
          });

          combinedHistoryEntries = [...uniqueHistoryEntries, ...uniqueSavedMatches].sort((a, b) => {
            const aTime = a.latestDate ? new Date(a.latestDate as string | number).getTime() : 0;
            const bTime = b.latestDate ? new Date(b.latestDate as string | number).getTime() : 0;
            return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
          });
        }
      } catch (error) {
        console.error("Saved edit history title search failed:", error);
      }
    }

    const filtered = historyFilter === "unsaved"
      ? combinedHistoryEntries.filter((entry) => !entry.saved)
      : combinedHistoryEntries;

    // Trim to pageSize when we oversampled; defer remainder to next scroll
    // page rather than trying to pre-fetch "enough" — the infinite list is
    // happy to request page N+1 once it mounts more rows.
    const startIndex = pageOffset * pageSize;
    const endIndex = startIndex + pageSize;
    const items = needsSavedTitleSearch
      ? filtered.slice(startIndex, endIndex)
      : needsClientFilter ? filtered.slice(0, pageSize) : filtered;

    return {
      items,
      // If the server says there's more, keep paging. If the server said
      // "done" but the client filter kept dropping items below pageSize,
      // hasMore is correctly driven by the server's authoritative flag.
      hasMore: needsSavedTitleSearch
        ? serverHasMore || savedSearchHasMore || filtered.length > endIndex
        : serverHasMore,
    };
  }, [capsuleNames, currentWorkspaceId, fetchSavedProjectsForHistory, historyFilter, savedHistoryMetadataReady, savedProjectIds, searchQuery]);

  /* ---- helper: relative time ---- */
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  /* Load non-library sections */
  useEffect(() => {
    if (activeSection === "transcribed") fetchTranscribedFiles();
  }, [activeSection, fetchTranscribedFiles]);

  /* ---- event handlers ---- */
  // Keep the latest activeFolder in a ref so the window-level create-content
  // listener does not need to be re-attached on every folder change.
  const activeFolderRef = useRef<Folder | null>(null);
  useEffect(() => {
    activeFolderRef.current = activeFolder;
  }, [activeFolder]);

  const handleNewProject = useCallback((folderId?: string) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      router.showAddAudioHightlightPageNew(
        null, null, null, null, true, true,
        null, null, null, null, null, true,
        null, null, true,
        null, // canSaveOnlyAfterTranscriptionAndEditing
        folderId || null, // defaultFolderId — pre-selects the folder in the Create Content save step
      );
    } catch (error) {
      console.error("Failed to navigate to new project:", error);
    }
  }, []);

  // Listen for the React-owns-the-trigger bridge from MyLibrary2PageView.
  // When the user clicks "Create Content" while viewing a folder, we carry
  // that folder id into the Create Content flow so it pre-selects in the picker.
  useEffect(() => {
    const onCreateContentRequested = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      handleNewProject(activeFolderRef.current?.id);
    };
    window.addEventListener("trebble:create-content-requested", onCreateContentRequested);
    return () => {
      window.removeEventListener("trebble:create-content-requested", onCreateContentRequested);
    };
  }, [handleNewProject]);

  const handleProjectClick = useCallback((id: string, item?: LibraryItem) => {
    requestStopInlinePlayback();
    // Legacy capsules created before the editable-project linkage exists
    // (~414/765 capsules on monsieur_ninja's staging account) have no
    // projectId. Opening them in the editor would send a capsuleId to the
    // backend's project access check and return a misleading 403 "You do
    // not have access to this project". Instead, route them through the
    // existing Transcribe & Edit flow so a project gets created on demand
    // (matches master's capsule context-menu UX, see PR #1165).
    if (!id) {
      if (!item) {
        console.warn("Missing library item for legacy capsule click; refusing to guess the target capsule.");
        window.alertWarningMessage?.(t(LEGACY_CAPSULE_NO_EDITABLE_PROJECT_MESSAGE_KEY));
        return;
      }
      try {
        const capsuleId = getLibraryItemCapsuleIdOrEmpty(item);
        const audioUrl = getLibraryItemPlaybackAudioUrl(item);
        const durationInSeconds = item.durationInSeconds || null;
        if (capsuleId && audioUrl) {
          PersistentModels.getInstance().getRouter().showTranscribeAndEditExistingCapsule(
            capsuleId,
            audioUrl,
            durationInSeconds,
            null,
          );
          return;
        }
        // No audio means we genuinely have nothing to transcribe — keep
        // the user-facing fallback message rather than silently swallowing
        // the click.
        window.alertWarningMessage?.(t(LEGACY_CAPSULE_NO_EDITABLE_PROJECT_MESSAGE_KEY));
      } catch (error) {
        console.error("Failed to route legacy capsule into Transcribe & Edit:", error);
      }
      return;
    }
    try {
      const router = PersistentModels.getInstance().getRouter();
      if (isSharedWithMeMode) {
        router.showCreateShortcastPageFromSharedProject(id, true);
      } else {
        router.editProjectInAudioEditor(id, true);
      }
    } catch (error) {
      console.error("Failed to navigate to project:", error);
    }
  }, [isSharedWithMeMode]);

  const handleProjectPlay = useCallback((id: string, item?: LibraryItem) => {
    if (window.trebbleAnalyticsHelper) {
      window.trebbleAnalyticsHelper.trackEvent("Library", "PlayProject", "Play project from library", null, { projectId: id });
    }
    // Play is triggered via project click for now
    handleProjectClick(id, item);
  }, [handleProjectClick]);

  const handleLegacyCapsuleDownload = useCallback(async (item: CapsuleLibraryItem) => {
    const uri = getLibraryItemDownloadAudioUrl(item);

    if (!uri) {
      console.warn("Skipping legacy capsule download because no processed audio URI is available.");
      window.alertWarningMessage?.(t(LEGACY_CAPSULE_NO_EDITABLE_PROJECT_MESSAGE_KEY));
      return;
    }

    const filename = getLibraryItemTitle(item) || "Untitled";
    await Utils.getInstance().downloadURI(uri, filename);
  }, []);

  /* Download needs the raw project/capsule entry so project and capsule IDs
     stay separate for export, deletion, and sharing flows. */
  const handleProjectDownload = useCallback((capsuleData?: LibraryItem, fallbackId?: string) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      const capsule: LibraryItem | undefined = capsuleData || cachedCapsules.find((entry) => {
        const projectId = getLibraryItemProjectIdOrEmpty(entry);
        const capsuleId = getLibraryItemCapsuleIdOrEmpty(entry);
        return projectId === fallbackId || capsuleId === fallbackId;
      });
      const videoUrl = getLibraryItemVideoUrl(capsule);
      const hasVideo = !!videoUrl;
      // Prefer the item's own projectId when we have the typed item.
      // If the caller passed a fallbackId (from the LibraryInfiniteList
      // render path this is getLibraryItemProjectIdOrEmpty(item)), it is
      // already the correct projectId — never the capsuleId — so using
      // it here does NOT reintroduce the Bug 1 cross-fallback.
      const projectId = getLibraryItemProjectIdOrEmpty(capsule) || (fallbackId || "");
      if (!projectId) {
        console.warn("Skipping library download because the item has no editable project to export.");
        const messageKey = capsule && getLibraryItemPlaybackAudioUrl(capsule)
          ? LEGACY_CAPSULE_REQUIRES_TRANSCRIPTION_MESSAGE_KEY
          : LEGACY_CAPSULE_NO_EDITABLE_PROJECT_MESSAGE_KEY;
        window.alertWarningMessage?.(t(messageKey));
        return;
      }
      const title = capsule ? getLibraryItemTitle(capsule) : "";
      router.showExportPopup(null, hasVideo, null, projectId, title, null, videoUrl);
    } catch (error) {
      console.error("Failed to trigger download:", error);
    }
  }, [cachedCapsules]);

  const createFolderWithName = useCallback(async (name: string): Promise<Folder | null> => {
    const trimmedName = name.trim();
    if (!trimmedName) return null;
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to create folders here.");
      return null;
    }

    const api = TrebbleClientAPI.getInstance();
    const depth = folderPath.length;
    const color = FOLDER_COLORS[(folders.length + depth) % FOLDER_COLORS.length];
    const response = await api.createProjectFolder({
      workspaceId: currentWorkspaceId || undefined,
      name: trimmedName,
      color,
      parentFolderId: activeFolder?.id || null,
    });
    const createdFolder = normalizeFolderListResponse(response)[0] || null;
    await loadFolders();
    await loadAllFolders();
    setFolderMutationTick((currentTick) => currentTick + 1);
    return createdFolder;
  }, [activeFolder?.id, canManageFolders, currentWorkspaceId, folders.length, loadFolders, loadAllFolders, normalizeFolderListResponse]);

  const handleMoveToFolder = useCallback((itemId: string, itemFolderId?: string | null) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("Only a workspace owner or admin can manage folders.");
      return;
    }
    setPendingMoveItemId(itemId);
    setPendingMoveFolderId(itemFolderId || null);
    const router = PersistentModels.getInstance().getRouter();
    router.showMoveToFolderPopup(null, {
      folders: allFolders,
      currentFolderId: itemFolderId || activeFolder?.id,
      onConfirm: async (folderId: string | null) => {
        try {
          const moveParams = {
            workspaceId: currentWorkspaceId || undefined,
            folderId: folderId || undefined,
            itemId: itemId,
          };
          await TrebbleClientAPI.getInstance().assignProjectFolderItem(moveParams);
          await loadFolders();
          await loadAllFolders();
          setFolderMutationTick((currentTick) => currentTick + 1);
          window.alertSuccessMessage?.(
            folderId ? "Project moved to folder." : "Project removed from folder.",
          );
        } catch (error) {
          console.error("Failed to move project to folder:", error);
          window.alertErrorMessage?.("Unable to update the folder for this project.");
        } finally {
          setPendingMoveItemId(null);
          setPendingMoveFolderId(null);
        }
      },
      ti18n: ti18n
    });
  }, [canManageFolders, allFolders, activeFolder?.id, currentWorkspaceId, loadFolders, loadAllFolders]);

  const handleProjectMenuAction = useCallback(async (id: string, action: string, capsuleData?: LibraryItem) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      const api = TrebbleClientAPI.getInstance();
      // `id` is the already-resolved projectId from the render path
      // (getLibraryItemProjectIdOrEmpty). It is never the capsuleId, so
      // `|| id` is safe here and not a reintroduction of Bug 1's
      // capsule-as-project fallback.
      const projectId = (capsuleData ? getLibraryItemProjectIdOrEmpty(capsuleData) : "") || id;
      const capsuleId = capsuleData ? getLibraryItemCapsuleIdOrEmpty(capsuleData) : "";
      if (isSharedWithMeMode) {
        if (action === "edit") {
          router.showCreateShortcastPageFromSharedProject(projectId, true);
        }
        return;
      }

      // Share routes differently based on whether the item has an editable
      // project: projectId → project-scoped ProjectSharePopup (guest invite +
      // access list). Legacy capsules (no projectId) → the simple capsule
      // share popup. Comments/Delete still require a capsuleId.
      if ((action === "comments" || action === "delete") && !capsuleId) {
        console.warn(`Skipping ${action} for project ${projectId} because no capsule id is available.`);
        return;
      }
      if (action === "share" && !projectId && !capsuleId) {
        console.warn("Skipping share because neither projectId nor capsuleId is available.");
        return;
      }

      switch (action) {
        case "edit":
          router.editProjectInAudioEditor(projectId, true);
          break;
        case "duplicate":
          router.duplicateProjectInAudioEditor(projectId, true);
          break;
        case "transcribe": {
          // Legacy "Continue without editing" capsules: route into the
          // Transcribe & Edit flow so a project gets created on demand.
          const audioUrl = capsuleData ? getLibraryItemPlaybackAudioUrl(capsuleData) : null;
          const durationInSeconds = capsuleData?.durationInSeconds || null;
          if (!capsuleId || !audioUrl) {
            console.warn(`Skipping transcribe action: missing ${!capsuleId ? "capsuleId" : "audioUrl"}.`);
            return;
          }
          router.showTranscribeAndEditExistingCapsule(capsuleId, audioUrl, durationInSeconds, null);
          break;
        }
        case "download": {
          if (projectId) {
            handleProjectDownload(capsuleData, projectId);
          } else if (capsuleData?.itemType === LIBRARY_ITEM_TYPES.CAPSULE) {
            await handleLegacyCapsuleDownload(capsuleData);
          } else {
            console.warn("Skipping download because no project id or capsule audio data is available.");
          }
          break;
        }
        case "share": {
          if (projectId) {
            const projectName = capsuleData ? getLibraryItemDisplayTitle(capsuleData) : "";
            router.showProjectSharePopup(projectId, projectName, null);
          } else {
            const api2 = TrebbleClientAPI.getInstance();
            api2.getCapsuleInfoByCapsuleId(capsuleId).then((capsuleModel: unknown) => {
              if (capsuleModel) {
                router.showSimpleSetCapsuleSharePopup(capsuleModel, null);
              }
            }).catch((err: unknown) => console.error("Failed to load capsule for share:", err));
          }
          break;
        }
        case "comments": {
          // Comments has a by-ID route that handles capsule retrieval
          router.showPageCommentsForCapsuleByCapsuleId(capsuleId, true);
          break;
        }
        case "delete": {
          const buttonLabels = [t("NO") || "No", t("YES") || "Yes"];
          window.showConfirmDialog?.(
            t("ARE_YOU_SURE_YOU_TO_DELETE_THIS_SONG_FROM_YOUR_LIBRARY"),
            (buttonIndex: number) => {
              if (buttonIndex === 2) {
                api.deleteSongCapsule(capsuleId).then(() => {
                  PersistentModels.getInstance().onCapsuleDeleted({ capsuleUID: capsuleId });
                  window.alertSuccessMessage?.(t("WAS_SUCCESSFULLY_DELETED"));
                }).catch((error: unknown) => {
                  const deleteErrorMessage = t("AN_ERROR_OCCURED_WHILE_DELETING");
                  const deleteNetworkErrorMessage = t("NO_INTERNET_CONNECTION_AVAILABLE");
                  const reportingError = normalizeDeleteErrorForReporting(
                    error,
                    deleteErrorMessage,
                    deleteNetworkErrorMessage,
                  );
                  const userFacingDeleteErrorMessage = getDeleteUserFacingErrorMessage(reportingError, deleteErrorMessage);
                  if (window.sendErrorToRaygun) {
                    window.sendErrorToRaygun(reportingError);
                  } else {
                    window.alertErrorMessage(reportingError);
                    return;
                  }
                  if (window.alertUserErrorMessage) {
                    window.alertUserErrorMessage(userFacingDeleteErrorMessage);
                  } else if (window.alertWarningMessage) {
                    window.alertWarningMessage(userFacingDeleteErrorMessage);
                  } else {
                    window.alert(userFacingDeleteErrorMessage);
                  }
                });
              }
            },
            t("DELETE_SONG"),
            buttonLabels,
            null,
            null,
          );
          break;
        }
        case "move": {
          // Look up the item's current folderId so the modal pre-selects
          // the correct folder even when viewing from root or search results.
          const moveSource: LibraryItem | undefined = capsuleData || cachedCapsules.find((entry) => {
            return getLibraryItemProjectIdOrEmpty(entry) === projectId || getLibraryItemCapsuleIdOrEmpty(entry) === projectId;
          });
          const itemFolderId = (moveSource?.folderId as string | undefined) || null;
          const folderItemId = getLibraryItemFolderAssignmentIdOrEmpty(
            moveSource || capsuleData,
            !!(isInWorkspaceContext && currentWorkspaceId),
          ) || projectId;
          await handleMoveToFolder(folderItemId, itemFolderId);
          break;
        }
      }
    } catch (error) {
      console.error("Failed to handle menu action:", error);
    }
  }, [cachedCapsules, currentWorkspaceId, handleLegacyCapsuleDownload, handleMoveToFolder, handleProjectDownload, isInWorkspaceContext, isSharedWithMeMode]);

  const handleFolderClick = useCallback((folderId: string) => {
    const folder = folders.find(f => f.id === folderId) || null;
    if (folder) {
      setFolderPath(prev => [...prev, folder]);
    }
    setActiveFolder(folder);
  }, [folders]);

  const handleNavigateToFolder = useCallback((folder: Folder) => {
    // Navigate to an ancestor folder — trim the path to that folder
    setFolderPath(prev => {
      const idx = prev.findIndex(f => f.id === folder.id);
      return idx >= 0 ? prev.slice(0, idx + 1) : prev;
    });
    setActiveFolder(folder);
  }, []);

  const handleNewFolder = useCallback(() => {
    setFolderNameModalOpen(true);
  }, []);

  const handleFolderNameConfirm = useCallback(async (name: string) => {
    setFolderNameModalOpen(false);
    try {
      await createFolderWithName(name);
    } catch (error) {
      console.error("Failed to create folder:", error);
      window.alertErrorMessage?.("Unable to create the folder.");
    }
  }, [createFolderWithName]);

  const handleDeleteFolder = useCallback(async (folder: Folder) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to delete folders.");
      return;
    }
    if (folder.itemCount > 0) {
      const folderMustBeEmptyMessage = t("FOLDER_MUST_BE_EMPTY_BEFORE_DELETE");
      if (window.alertUserErrorMessage) {
        window.alertUserErrorMessage(folderMustBeEmptyMessage);
      } else if (window.alertWarningMessage) {
        window.alertWarningMessage(folderMustBeEmptyMessage);
      } else {
        window.alert(folderMustBeEmptyMessage);
      }
      return;
    }
    const buttonLabels = [t("NO") || "No", t("YES") || "Yes"];
    window.showConfirmDialog?.(
      `${t("DELETE_FOLDER_CONFIRM_MESSAGE")} "${folder.name}"?`,
      async (buttonIndex: number) => {
        if (buttonIndex !== 2) return;
        try {
          const api = TrebbleClientAPI.getInstance();
          await api.deleteProjectFolder({
            folderId: folder.id,
            workspaceId: currentWorkspaceId || undefined,
          });
          await loadFolders();
          await loadAllFolders();
          setFolderMutationTick((currentTick) => currentTick + 1);
          // Silent success — no popup for folder operations
        } catch (error) {
          console.error("Failed to delete folder:", error);
          window.alertErrorMessage?.("Unable to delete the folder.");
        }
      },
      t("DELETE_FOLDER"),
      buttonLabels,
    );
  }, [canManageFolders, currentWorkspaceId, loadFolders, loadAllFolders]);

  const handleRenameFolder = useCallback((folder: Folder) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to rename folders.");
      return;
    }
    setPendingRenameFolder(folder);
    setRenameFolderModalOpen(true);
  }, [canManageFolders]);

  const handleRenameFolderConfirm = useCallback(async (newName: string) => {
    setRenameFolderModalOpen(false);
    if (!pendingRenameFolder) return;
    try {
      const api = TrebbleClientAPI.getInstance();
      await api.renameProjectFolder({
        folderId: pendingRenameFolder.id,
        name: newName,
        workspaceId: currentWorkspaceId || undefined,
      });
      await loadFolders();
      await loadAllFolders();
      setFolderMutationTick((currentTick) => currentTick + 1);
      // Silent success — no popup for folder operations
    } catch (error) {
      console.error("Failed to rename folder:", error);
      window.alertErrorMessage?.("Unable to rename the folder.");
    } finally {
      setPendingRenameFolder(null);
    }
  }, [currentWorkspaceId, loadFolders, loadAllFolders, pendingRenameFolder]);

  const handleMoveFolderToFolder = useCallback((folder: Folder) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to move folders.");
      return;
    }
    setPendingMoveFolder(folder);
    const filteredFolders = allFolders.filter((f) => !getFolderAndDescendantIds(folder.id, allFolders).has(f.id));
    const router = PersistentModels.getInstance().getRouter();
    router.showMoveToFolderPopup(null, {
      folders: filteredFolders,
      currentFolderId: undefined,
      onConfirm: async (targetFolderId: string | null) => {
        try {
          const api = TrebbleClientAPI.getInstance();
          await api.moveProjectFolder({
            folderId: folder.id,
            parentFolderId: targetFolderId || undefined,
            workspaceId: currentWorkspaceId || undefined,
          });
          await loadFolders();
          await loadAllFolders();
          setFolderMutationTick((currentTick) => currentTick + 1);
        } catch (error) {
          console.error("Failed to move folder:", error);
          window.alertErrorMessage?.("Unable to move the folder.");
        } finally {
          setPendingMoveFolder(null);
        }
      },
      ti18n: ti18n
    });
  }, [canManageFolders, allFolders, currentWorkspaceId, loadFolders, loadAllFolders]);

  const handleEditFile = useCallback((fileId: string) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      router.showCreateShortcastPageFromTranscription(fileId);
    } catch (error) {
      console.error("Failed to navigate to edit file:", error);
    }
  }, []);

  const handleRecover = useCallback((entryId: string) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      router.duplicateProjectInAudioEditor(entryId, true);
    } catch (error) {
      console.error("Failed to recover entry:", error);
    }
  }, []);

  const handleRestore = useCallback((entryId: string, _version: number) => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      router.duplicateProjectInAudioEditor(entryId, true);
    } catch (error) {
      console.error("Failed to restore version:", error);
    }
  }, []);

  const handleSectionChange = useCallback((_key: string) => {
    // Section changes are now handled via JQM page navigation.
    // This callback is kept for backward compatibility but is a no-op.
  }, []);

  const handleSettingsClick = useCallback(() => {
    try {
      const router = PersistentModels.getInstance().getRouter();
      router.showSettingsPage();
    } catch (error) {
      console.error("Failed to navigate to settings:", error);
    }
  }, []);

  /* ---- section‑specific title and filters ---- */
  const getSectionTitle = (): string => {
    switch (activeSection) {
      case "library":
        return getWorkspaceAwareProjectLabel();
      case "transcribed":
        return t("TRANSCRIBED_FILES");
      case "history":
        return t("EDIT_HISTORY");
      default:
        return t("PROJECTS");
    }
  };

  const getFilters = () => {
    switch (activeSection) {
      case "library":
        return LIBRARY_FILTERS;
      case "transcribed":
        return TRANSCRIBED_FILTERS;
      default:
        return [];
    }
  };

  const getSortOptions = () => {
    if (activeSection === "library") {
      return LIBRARY_SORTS;
    }
    return [];
  };

  /* ---- render content area ---- */
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ padding: "24px 0" }}>
          <TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 6 }} />
        </div>
      );
    }

    switch (activeSection) {
      case "library":
        return (
          <>
            {shouldShowFolderSection && (
              <div style={{ marginBottom: 24 }}>
                <h3 className="library-section-label">{t("FOLDERS")}</h3>
                <FolderRow
                  folders={folders}
                  onFolderClick={handleFolderClick}
                  onNewFolder={handleNewFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onRenameFolder={handleRenameFolder}
                  onMoveFolder={handleMoveFolderToFolder}
                  canCreateFolder={canManageFolders}
                  ti18n={ti18n}
                  hideItemCount={workspaceProjectFilter === WORKSPACE_FILTER_OWNED || activeFilter !== "all"}
                />
              </div>
            )}

            {/* Projects section */}
            <div>
              <h3 className="library-section-label">{getWorkspaceAwareProjectLabel()}</h3>
              <TrebbleInfiniteList
                key={`library-${currentWorkspaceId || "personal"}-${workspaceProjectFilter || "all"}-${activeFolder?.id || "root"}-${folderMutationTick}-${viewMode}-${appliedSearch}-${activeFilter}-${activeSort}`}
                containerId="libraryInfiniteScroll"
                containerClassname={viewMode === "grid" ? "library-infinite-grid" : "library-infinite-list"}
                containerHeightMargin={340}
                pageSize={PAGE_SIZE}
                fetchData={fetchLibraryData}
                loader={<TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 3 }} />}
                locale={{
                  emptyText: (
                    <EmptyState
                      message={getWorkspaceAwareEmptyMessage()}
                      actionLabel={isSharedWithMeMode ? undefined : t("CREATE_CAPSULE")}
                      onAction={isSharedWithMeMode ? undefined : () => handleNewProject(activeFolder?.id)}
                      actionIconClassName={isSharedWithMeMode ? undefined : "ion-ios7-plus-empty"}
                    />
                  ),
                }}
                renderItem={(rawItem: Record<string, unknown>) => {
                  // TrebbleInfiniteList is untyped — but fetchLibraryData
                  // returns LibraryItem[], so we know the discriminator is
                  // already stamped by the API-wrapper layer. Cast once.
                  const item = rawItem as unknown as LibraryItem;
                  const card = mapCapsuleToCard(item);
                  // getOpenableProjectId returns null for legacy pre-project
                  // capsules. Pass "" + the item so handleProjectClick can
                  // route them into the Transcribe & Edit flow instead of
                  // hitting a 403-as-modal dead end (see Bug 1 history).
                  const projectId = getLibraryItemProjectIdOrEmpty(item);
                  const isLegacyCapsuleNeedingTranscription =
                    !!getLibraryItemCapsuleIdOrEmpty(item) &&
                    !projectId &&
                    !!getLibraryItemPlaybackAudioUrl(item);
                  return (
                    <ProjectCard
                      key={card.id}
                      {...card}
                      viewMode={viewMode}
                      ti18n={ti18n}
                      primaryActionLabel={
                        isSharedWithMeMode
                          ? t("OPEN")
                          : isLegacyCapsuleNeedingTranscription
                            ? t("TRANSCRIBE_AND_EDIT")
                            : undefined
                      }
                      primaryActionIconClassName={
                        isSharedWithMeMode
                          ? "pe-7s-right-arrow"
                          : isLegacyCapsuleNeedingTranscription
                            ? "pe-7s-news-paper"
                            : undefined
                      }
                      showDownloadButton={!isSharedWithMeMode && canDownloadLibraryItem(item)}
                      menuActions={getProjectCardMenuActions(item)}
                      onPlay={() => handleProjectPlay(projectId, item)}
                      onDownload={() => handleProjectDownload(item, projectId)}
                      onClick={() => handleProjectClick(projectId, item)}
                      onMenuAction={(_cardId: string, action: string) => handleProjectMenuAction(projectId, action, item)}
                    />
                  );
                }}
              />
            </div>
          </>
        );

      case "transcribed":
        return (
          <div style={{ marginTop: 20 }}>
            {/* Table header — rendered once above the scrolling list */}
            <TranscribedFileTable
              files={[]}
              onEdit={handleEditFile}
              ti18n={ti18n}
            />
              <TrebbleInfiniteList
              key={`transcribed-${currentWorkspaceId || "personal"}-${searchQuery}-${activeFilter}`}
              containerId="transcribedInfiniteScroll"
              containerClassname="library-infinite-list"
              containerHeightMargin={200}
              pageSize={PAGE_SIZE}
              fetchData={fetchTranscribedData}
              loader={<TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 3 }} />}
              locale={{ emptyText: <EmptyState message={t("NO_TRANSCRIPTION_FOUND")} /> }}
              renderItem={(item: Record<string, unknown>) => {
                const file = item as unknown as TranscribedFile;
                return (
                  <TranscribedFileTable
                    key={file.id}
                    files={[file]}
                    onEdit={handleEditFile}
                    ti18n={ti18n}
                    hideHeader
                  />
                );
              }}
            />
          </div>
        );

      case "history":
        return (
          <div style={{ marginTop: 20 }}>
            {/* Filter + Search */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <TrebbleFilterButtons
                filters={[
                  { key: "all", label: t("ALL") },
                  { key: "unsaved", label: t("UNSAVED_DRAFTS") },
                  { key: "saved", label: t("SAVED_HISTORY") },
                ]}
                activeFilter={historyFilter}
                onFilterChange={(f) => setHistoryFilter(f as "all" | "unsaved" | "saved")}
                useEditorTheme={false}
              />
              <TrebbleInput
                useEditorTheme={false}
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                placeholder={t("SEARCH")}
                allowClear
                style={{ width: 220 }}
                size="middle"
              />
	            </div>

	            {/* Progressive list */}
	            <div>
	              {!savedHistoryMetadataReady && historyFilter !== "saved" ? (
	                <div style={{ padding: "12px 0" }}>
	                  <TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 4 }} />
	                </div>
	              ) : (
	                <TrebbleInfiniteList
	                  key={`history-${currentWorkspaceId || "personal"}-${searchQuery}-${historyFilter}-${savedProjectIds.size}-${savedHistoryMetadataReady ? "ready" : "loading"}`}
	                  containerId="editHistoryInfiniteScroll"
	                  containerClassname="library-infinite-list"
	                  containerHeightMargin={160}
	                  pageSize={PAGE_SIZE}
	                  fetchData={fetchHistoryData}
	                  loader={<TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 3 }} />}
	                  locale={{ emptyText: <EmptyState message={t("NO_EDITS_FOUND")} /> }}
	                  renderItem={(item: Record<string, unknown>) => {
	                    const latestId = item._id as string;
	                    const refId = (item.refId || latestId) as string;
	                    const isSaved = item.saved as boolean;
	                    const editCount = (item.editCount || 1) as number;
	                    const latestDate = item.latestDate ? new Date(item.latestDate as string | number) : null;
	                    const capsuleTitle = (item.name || "") as string;
	                    const displayName = capsuleTitle || formatHistoryTimestamp(latestDate);
	                    const versions = (item.versions || []) as Array<Record<string, unknown>>;
	                    const hasVersions = versions.length > 1;
	                    const isExpanded = expandedRefIds.has(refId);

	                    return (
	                      <div key={refId} style={{ backgroundColor: "var(--bg-default)", borderBottom: "1px solid var(--border-default-color, #eee)" }}>
	                        {/* Main row */}
	                        <div style={{ display: "flex", alignItems: "center", padding: "14px 16px", gap: 12 }}>
	                          {/* Expand chevron */}
	                          {hasVersions ? (
	                            <button
	                              type="button"
	                              onClick={() => setExpandedRefIds(prev => {
	                                const next = new Set(prev);
	                                if (next.has(refId)) next.delete(refId); else next.add(refId);
	                                return next;
	                              })}
	                              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, padding: 0, border: "none", backgroundColor: "transparent", cursor: "pointer", flexShrink: 0 }}
	                            >
	                              <svg width={12} height={12} viewBox="0 0 12 12" fill="none" style={{ transition: "transform 0.15s ease", transform: isExpanded ? "rotate(180deg)" : "none" }}>
	                                <path d="M3 4.5l3 3 3-3" stroke="var(--text-muted)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
	                              </svg>
	                            </button>
	                          ) : (
	                            <div style={{ width: 20, flexShrink: 0 }} />
	                          )}

	                          {/* Name + meta */}
	                          <div style={{ flex: 1, minWidth: 0 }}>
	                            <div style={{ fontSize: "var(--text-base)", fontWeight: 700, color: "var(--text-default-color)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 8 }}>
	                              {displayName}
	                              {isSaved && (
	                                <span style={{ fontSize: 11, padding: "2px 8px", backgroundColor: "rgba(76, 175, 80, 0.1)", color: "var(--trebble-success-color, #4caf50)", fontWeight: 700 }}>
	                                  {t("SAVED")}
	                                </span>
	                              )}
	                            </div>
	                            <div style={{ fontSize: "var(--text-sm, 12px)", color: "var(--text-muted)", marginTop: 2 }}>
	                              {editCount} {t("EDITS")} &middot; {t("LAST")} {latestDate ? getRelativeTime(latestDate) : ""}
	                            </div>
	                          </div>

	                          {/* Recover button */}
	                          <TrebbleButton
	                            useEditorTheme={false}
	                            type="link"
	                            onClick={() => handleRecover(latestId)}
	                            style={{ color: "var(--trebble-primary, #EC7F54)", fontWeight: 700, fontSize: "var(--text-base)", padding: 0 }}
	                          >
	                            {t("RECOVER")}
	                          </TrebbleButton>
	                        </div>

	                        {/* Expanded versions */}
	                        {isExpanded && hasVersions && (
	                          <div style={{ padding: "4px 16px 12px 52px" }}>
	                            <TrebbleTimeline
	                              useEditorTheme={false}
	                              items={versions.map((v) => {
	                                const vDate = v.date ? new Date(v.date as string | number) : null;
	                                return {
	                                  color: "#EC7F54",
	                                  children: (
	                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
	                                      <span style={{ fontSize: "var(--text-base)", color: "var(--text-muted)", minWidth: 24 }}>
	                                        v{v.version as number}
	                                      </span>
	                                      <span style={{ fontSize: "var(--text-base)", color: "var(--text-default-color)", flex: 1 }}>
	                                        {vDate ? formatHistoryTimestamp(vDate) : ""} &middot; {t("AUTO_SAVE")}
	                                      </span>
	                                      <TrebbleButton
	                                        useEditorTheme={false}
	                                        size="small"
	                                        type="link"
	                                        onClick={() => handleRestore(v._id as string, v.version as number)}
	                                        style={{ color: "var(--trebble-primary, #EC7F54)", fontSize: "var(--text-base)", fontWeight: 700, padding: "0 4px" }}
	                                      >
	                                        {t("RESTORE")}
	                                      </TrebbleButton>
	                                    </div>
	                                  ),
	                                };
	                              })}
	                            />
	                          </div>
	                        )}
	                      </div>
	                    );
	                  }}
	                />
	              )}
	            </div>
	          </div>
	        );

      default:
        return null;
    }
  };

  /* ---- filtered transcribed files ---- */
  const filteredTranscribedFiles = (() => {
    let filtered = transcribedFiles;
    if (activeFilter === "completed") filtered = filtered.filter(f => f.status === "Completed");
    else if (activeFilter === "failed") filtered = filtered.filter(f => f.status === "Failed");
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(f => f.filename.toLowerCase().includes(q));
    }
    return filtered;
  })();

  /* ---- main render ---- */
  /* The JQM shell provides the page header (title, hamburger, search).
     We render: filter bar + content area.
     Navigation between sections is handled by separate JQM pages.
     Reset CSS variables so they work in both light and dark JQM pages. */
  const handleSearchSubmit = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setAppliedSearch(searchQuery.trim());
    setSearchLoading(true);
    setIsSearchResultsView(true);
    try {
      // Search across the entire workspace/personal space, not just the current folder
      const results = await fetchProjectsForActiveContext(searchQuery, 0, isSharedWithMeMode ? 250 : 50, {
        folderId: null,
      });
      setSearchResults(filterAndSortLibraryProjects(results || []));
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [fetchProjectsForActiveContext, filterAndSortLibraryProjects, isSharedWithMeMode, searchQuery]);

  const handleCloseSearch = useCallback(() => {
    setIsSearchResultsView(false);
    setSearchResults([]);
    setSearchQuery("");
    setAppliedSearch("");
  }, []);

  const handleSearchLocationClick = useCallback((folderId: string | null) => {
    requestStopInlinePlayback();

    if (!folderId) {
      setActiveFolder(null);
      setFolderPath([]);
    } else {
      const folder = allFolders.find(f => f.id === folderId) || folders.find(f => f.id === folderId);
      if (!folder) {
        return;
      }
      setActiveFolder(folder);
      setFolderPath(getAncestorPath(folder, allFolders));
    }

    setSearchQuery("");
    setAppliedSearch("");
    setIsSearchResultsView(false);
    setSearchResults([]);
  }, [allFolders, folders]);

  const handleSuggestionClick = useCallback((suggestion: { id: string; type: string }) => {
    if (suggestion.type === "folder") {
      const folder = allFolders.find(f => f.id === suggestion.id) || folders.find(f => f.id === suggestion.id);
      if (folder) {
        setActiveFolder(folder);
        setFolderPath(getAncestorPath(folder, allFolders));
      }
      setSearchQuery("");
      setAppliedSearch("");
      setIsSearchResultsView(false);
      setSearchResults([]);
      return;
    }
    // Preserve LibraryItem so legacy capsules (without projectId) route through
    // the Transcribe & Edit flow instead of failing the project access check.
    const matchedItem = cachedCapsules.find(
      (c) =>
        getLibraryItemProjectIdOrEmpty(c) === suggestion.id ||
        getLibraryItemCapsuleIdOrEmpty(c) === suggestion.id
    );
    handleProjectClick(suggestion.id, matchedItem);
  }, [allFolders, folders, cachedCapsules, handleProjectClick]);

  /* Build search suggestions from ALL folders + ALL cached capsules (workspace-wide) */
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const results: Array<{ id: string; name: string; type: "folder" | "audio" | "video"; folderName?: string; folderColor?: string; itemCount?: number }> = [];

    for (const f of allFolders) {
      if (f.name.toLowerCase().includes(q)) {
        results.push({ id: f.id, name: f.name, type: "folder", folderColor: f.color, itemCount: f.itemCount });
      }
    }

    // Match projects from ALL cached capsules (limit to 5)
    let projectCount = 0;
    for (const c of allCachedCapsules) {
      if (projectCount >= 5) break;
      const title = getLibraryItemDisplayTitle(c);
      if (!title.toLowerCase().includes(q)) continue;
      const hasVideo = !!getLibraryItemVideoUrl(c);
      const resolvedProjectId = getLibraryItemProjectIdOrEmpty(c);
      if (!resolvedProjectId) {
        // Legacy capsule with no editable project — skip from
        // suggestions so a click can't fall through to a 403.
        continue;
      }
      results.push({
        id: resolvedProjectId,
        name: title,
        type: hasVideo ? "video" : "audio",
      });
      projectCount++;
    }

    return results;
  }, [allCachedCapsules, allFolders, searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);

  return (<>
    <div
      style={{
        width: "100%",
        color: "var(--text-default-color)",
      }}
    >
      {/* Main content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 32px" }}>
        {activeSection === "library" ? (
          <>
            {/* Google Drive-style search bar with filter popup */}
            <div style={{ padding: "12px 0 0" }}>
              <LibrarySearchBar
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
                onFilterClick={() => setFilterOpen(!filterOpen)}
                placeholder={t("SEARCH")}
                ti18n={ti18n}
                suggestions={searchSuggestions}
                showSuggestions={searchSuggestions.length > 0}
                onSuggestionClick={handleSuggestionClick}
                itemSectionLabel={t("PROJECTS")}
                filterElement={
                  <LibraryFilterPopup
                    open={filterOpen}
                    onOpenChange={setFilterOpen}
                    activeFilter={activeFilter}
                    onFilterChange={setActiveFilter}
                    activeSort={activeSort}
                    onSortChange={setActiveSort}
                    ti18n={ti18n}
                    typeOptions={isSharedWithMeMode ? [] : undefined}
                  >
                    <button
                      type="button"
                      onClick={() => setFilterOpen(!filterOpen)}
                      aria-label="Open filters"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 4,
                        margin: 0,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "visible",
                      }}
                    >
                      <span style={{ position: "relative", display: "inline-flex", overflow: "visible" }}>
                        <i className="pe-7s-filter" style={{ WebkitTextStroke: "0.5px", fontSize: "var(--text-lg)", color: filterOpen || activeFilter !== "all" || activeSort !== "newest" ? "var(--trebble-primary, #EC7F54)" : "var(--text-muted)", lineHeight: 1 }} />
                        {(activeFilter !== "all" || activeSort !== "newest") && (
                          <span style={{ position: "absolute", top: 0, right: 0, width: 8, height: 8, backgroundColor: "var(--trebble-primary, #EC7F54)", borderRadius: "50%", transform: "translate(50%, -50%)" }} />
                        )}
                      </span>
                    </button>
                  </LibraryFilterPopup>
                }
              />
            </div>

            {/* Search results view OR normal toolbar + content */}
            {isSearchResultsView ? (
              <LibrarySearchResults
                query={searchQuery}
                results={searchResults.map((c) => {
                  const hasVideo = !!getLibraryItemVideoUrl(c);
                  const sharedProject = c.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? c.project : null;
                  const createdDate = c.createdDate || c.creationDate || c.lastUpdatedDate || (c.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? c.grantedDate : undefined) || (sharedProject ? sharedProject.lastUpdatedDate : undefined) || (sharedProject ? sharedProject.createdDate : undefined);
                  const dateStr = createdDate ? new Date(createdDate as string | number).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
                  const secs = c.durationInSeconds;
                  const dur = secs && secs > 0 ? `${Math.floor(secs / 60)}:${Math.floor(secs % 60).toString().padStart(2, "0")}` : undefined;
                  // Bug 2: resolve the parent folder (or root) from
                  // allFolders.itemIds. Previously we hard-coded the root
                  // label for every search hit so a project inside
                  // "Marketing" was mis-reported as living under "Projects".
                  // Folder-assignment IDs use the same rule as the folder
                  // picker (capsule id for personal scope, project id for
                  // workspace scope).
                  const assignmentId = getLibraryItemFolderAssignmentIdOrEmpty(c, isInWorkspaceContext);
                  const assignedParentFolder = assignmentId
                    ? allFolders.find((f) => (f.itemIds || []).includes(assignmentId))
                    : undefined;
                  const directParentFolder = c.folderId
                    ? allFolders.find((f) => f.id === c.folderId)
                    : undefined;
                  const parentFolder = assignedParentFolder || directParentFolder;
                  return {
                    id: getLibraryItemProjectIdOrEmpty(c),
                    name: getLibraryItemTitle(c) || "Untitled",
                    type: hasVideo ? "video" as const : "audio" as const,
                    date: dateStr,
                    duration: dur,
                    folderId: parentFolder ? parentFolder.id : null,
                    folderName: parentFolder ? parentFolder.name : getWorkspaceAwareRootLabel(),
                    folderColor: parentFolder ? parentFolder.color : undefined,
                  };
                })}
                onClose={handleCloseSearch}
                onResultClick={(resultId: string) => {
                  // Preserve LibraryItem so legacy capsules (id === "")
                  // can be routed through the Transcribe & Edit flow.
                  const matchedItem = searchResults.find(
                    (c) =>
                      getLibraryItemProjectIdOrEmpty(c) === resultId ||
                      getLibraryItemCapsuleIdOrEmpty(c) === resultId
                  );
                  handleProjectClick(resultId, matchedItem);
                }}
                onLocationClick={handleSearchLocationClick}
                loading={searchLoading}
                ti18n={ti18n}
              />
            ) : (
              <>
                {/* Toolbar: breadcrumb + view toggle */}
                <LibraryToolbar
                  activeFolder={activeFolder}
                  folderPath={folderPath}
                  onNavigateRoot={() => { setActiveFolder(null); setFolderPath([]); }}
                  onNavigateToFolder={handleNavigateToFolder}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  ti18n={ti18n}
                  rootLabel={getWorkspaceAwareRootLabel()}
                  showBreadcrumb={!isSharedWithMeMode}
                />
                {isInWorkspaceContext && (
                  <div style={{ margin: "0 0 24px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <TrebbleFilterButtons
                      filters={[
                        { key: WORKSPACE_FILTER_ALL, label: t("ALL") },
                        { key: WORKSPACE_FILTER_OWNED, label: t("OWNED_BY_ME_TAB") },
                      ]}
                      activeFilter={activeWorkspaceProjectFilter}
                      onFilterChange={setWorkspaceProjectScope}
                      useEditorTheme={false}
                    />
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          /* Other sections keep the old FilterBar */
          getFilters().length > 0 && (
            <FilterBar
              filters={getFilters()}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              sortOptions={getSortOptions()}
              activeSort={activeSort}
              onSortChange={setActiveSort}
              searchQuery={activeSection === "transcribed" ? searchQuery : undefined}
              onSearchChange={activeSection === "transcribed" ? setSearchQuery : undefined}
              searchPlaceholder={t("SEARCH")}
              ti18n={ti18n}
            />
          )
        )}
        {!isSearchResultsView && renderContent()}
      </div>
    </div>
    <FolderNameModal
      open={folderNameModalOpen}
      onConfirm={handleFolderNameConfirm}
      onCancel={() => setFolderNameModalOpen(false)}
      ti18n={ti18n}
    />
    <FolderNameModal
      open={renameFolderModalOpen}
      title={t("FOLDER_RENAME_FOLDER")}
      okText={t("FOLDER_RENAME")}
      initialValue={pendingRenameFolder?.name || ""}
      onConfirm={handleRenameFolderConfirm}
      onCancel={() => { setRenameFolderModalOpen(false); setPendingRenameFolder(null); }}
      ti18n={ti18n}
    /></>
  );
};

export default LibraryPage;
